export enum GTMEventName {
  "회원가입" = "signin",
  "후기 클릭" = "후기 클릭",
  "click_kakao-login(match)" = "click_kakao-login(match)",
  "로그인/회원가입 클릭" = "로그인/회원가입 클릭",
}

// GTM properties에서 금지된 필드 (내부적으로 설정되는 값들)
type ForbiddenGTMKeys = "event" | "text" | "user_id";

// 안전한 GTM properties 타입: 금지 필드를 제외한 key-value
export type SafeGTMProperties = {
  [key: string]: boolean | string | number;
} & {
  [K in ForbiddenGTMKeys]?: never;
};

// 서로 다른 층위가 결합될 때는 언더바 사용. 같은 층위 내에서는 언더바도 대쉬로 바꾼다.
// (parlor_detail을 다른 층위와 엮어서 쓸 때는 parlor-detail)
export type CommonEventProps = ActionEvent | ClickEventType;

export interface ClickEventType {
  action?: "click" | "experiment_exposure";
  path:
    | keyof typeof EventPathType
    | keyof typeof DirectEventPathType
    | keyof typeof RefundServicePathType
    | keyof typeof MissParlorEventPathType;
  trigger_and_target:
    | EventTriggerAndTargetType[EventPathType]
    | DirectEventTriggerAndTargetType[DirectEventPathType]
    | RefundServiceTriggerAndTargetType[RefundServicePathType]
    | MissParlorEventTriggerAndTargetType[MissParlorEventPathType];
  text?: string;
  properties?: SafeGTMProperties;
}

export interface ActionEvent {
  action:
    | "signup"
    | "signup_kakao"
    | "signup_easy"
    | "login"
    | "call"
    | "goi_call"
    | "match_request_complete"
    | "match_request_complete_parlor"
    | "signup_or_login_success"
    | `signup_status_${string}`
    | "uv_revisit_modal"
    | "revisit_modal_cta"
    | "revisit_modal_close"
    | "uv_revisit_parlor_modal"
    | "uv_care_facility_lead_modal"
    | "revisit_parlor_modal_cta-question"
    | "revisit_parlor_modal_cta-uv_prlr_regin"
    | `${string}_flower_order_mayflower_try`
    | `${string}_flower_order_closed_mayflower_payment`
    | "open_parlor50_modal"
    | "abnormal_access_registration"
    | "consult-button-click"
    | "b2b-submit"
    | "self-funeral-submit"
    | "apply-flower"
    | "match_request_start"
    | "match_request_price_start"
    | "match_request_procedure_start"
    | "match_request_price_procedure_start"
    | "match_request_facility_start"
    | "match_request_procedure_complete"
    // now 콜 확대 임종 배너 공통 클릭. 페이지·버튼은 properties.page / properties.source 로 구분
    | "urgent_call_click"
    | "match_request_procedure_submit_success"
    | "match_request_answer_first_question"
    | "mubinso_request_complete"
    | "mubinso_funnel_enter"
    | "mubinso_funnel_step"
    | "experiment_exposure"
    | "revisit_ctx-signup"
    /**
     * v2 통합 리드(POST /v2/leads/) 제출 성공. path 무관.
     * postLeadV2 래퍼(platform, ssr-platform)와 parlor raw 호출부에서만 발화한다.
     * 화면별 성공 이벤트(submit_success, quote_success 등)는 그대로 유지. 이건 집계용.
     *
     * 총량과 리드종류별을 같이 쏜다 (견적의 match_request_complete + guest_submit_* 와 같은 패턴).
     * - lead_submit: 총량. properties.lead_type 로도 구분 가능
     * - lead_submit_{checklist|home}: 리드종류별. GTM/Hackle 에서 프로퍼티 필터 없이 바로 쓸 용도
     */
    | "lead_submit"
    | "lead_submit_checklist"
    | "lead_submit_home"
    /**
     * 광고 전환가치 재설계용 신규 이벤트 세트(2026-09).
     * 기존 이벤트는 그대로 두고 같은 자리에서 추가로 발화한다. 기존 캠페인 영향 없음.
     *
     * - goi_funnel_complete: 견적 퍼널 완주(비회원 제출). 장지추천 제출은 제외된다
     *   (`fireGuestSubmitEvents` 안에서만 쏘는데, 장지추천 제출부는 그 함수를 부르지 않는다).
     * - goi_lead_{임박도}: 리드 제출. 견적(가입 시점) + v2 리드(체크리스트/홈질문/무빈소) 공통.
     *   채널로는 나누지 않는다 — 같은 임박도 안에서 채널별 출동전환율 차이가 없다.
     * - goi_lead_unknown: 폼에 임박도 문항이 없는 화면에서 들어온 v2 리드.
     */
    | "goi_funnel_complete"
    | "goi_lead_now"
    | "goi_lead_week"
    | "goi_lead_month"
    | "goi_lead_notyet"
    | "goi_lead_unknown"
    | CompositeEventTypes
    | TrackingServiceEventTypes
    | GuestAndSignupEventType;
  properties?: SafeGTMProperties;
}
export type GuestAndSignupEventType =
  /** 비회원 견적  */
  | "match_request_complete" // 기본
  | "guest_submit_now" // 임종
  | "guest_submit_week" // 임박
  | "guest_submit_month" // 1달
  | "guest_submit_notyet" // 급하지 않음
  /** 검색광고 */
  | "guest_search_needs" // 검색광고
  /** 견적페이지 */
  | "guest_landing_parlor" // 장례식장
  | "guest_landing_facility" // 장지
  | "guest_landing_crematorium" // 화장장
  // | "guest_landing_guidebook" // 가이드북
  /** 회원가입 */
  | "signup_status_now" // 임종
  | "signup_status_week" // 임박
  | "signup_status_month" // 1달
  | "signup_status_notyet" // 여유
  /** 검색광고 */
  | "signup_search_needs" // 검색광고 -
  /** 페이지 */
  | "signup_landing_parlor" // 장례식장
  | "signup_landing_facility" // 장지
  | "signup_landing_crematorium"; // 화장장
// | "signup_landing_guidebook"; // 가이드북

export type CompositeEventTypes =
  | "funnel-parlor_rqst-region-signup"
  | "funnel-uv_match_rqst-submit_signup";

export type TrackingServiceEventTypes =
  | "tracking_event-uv_redirect_url"
  | `tracking_event-uv_redirect_url_${"A" | "B" | "AB"}`;

export enum DirectEventPathType {
  "gnb" = "gnb",
  "home" = "home",
  "product_list" = "product_list",
  "product_detail" = "product_detail",
  "registration" = "registration",
  "b2b" = "b2b",
  "introduce" = "introduce",
  "introduce_revisit" = "introduce_revisit",
  "home_honam" = "home_honam",
  "home_dggb" = "home_dggb",
  "content" = "content",
  "feed" = "feed",
  "content_sn_1" = "content_sn_1",
  "content_sn_2" = "content_sn_2",
  "content_care" = "content_care",
  "faq" = "faq",
  "faq_detail" = "faq_detail",
  "home_female" = "home_female",
  "coupon" = "coupon",
  "comparison" = "comparison",
  "closed" = "closed",
  "half" = "half",
  "saving" = "saving",
  "toss" = "toss",
  "contract_check" = "contract_check",
  "benefit" = "benefit",
  "sangjo_join" = "sangjo_join",
  "prepare_content" = "prepare_content",
  "call_lead" = "call_lead",
  "calculator" = "calculator",
  "prime_request" = "prime_request",
  "paybk" = "paybk",
  "appintoss" = "appintoss",
  "gasapp" = "gasapp",
  "calculator_daangn" = "calculator_daangn",
  "calculator_caring" = "calculator_caring",
  "referral" = "referral",
  "readiness_quiz" = "readiness_quiz",
  "introduce_quiz" = "introduce_quiz",
  "lead_leadform" = "lead_leadform",
  "contract_oxquiz" = "contract_oxquiz",
  "prime_benefit" = "prime_benefit",
  // 겨울 효도여행 랜딩(/prime-benefit/trip-winter). 마이리얼트립 제휴 숙소 카드 클릭
  "lp_trip_winter" = "lp_trip_winter",
  // 겨울 케어 선물 랜딩(/prime-benefit/winter-care-gift). 카카오 선물하기 상품 카드 클릭
  "lp_winter_care_gift" = "lp_winter_care_gift",
  "family_share" = "family_share",
  // 가족사진 이벤트(시그널스튜디오). 랜딩 4버전 비교가 목적이라 variant 를 path 에
  // 둔다 — `click-fam_photo_a-%` 처럼 prefix 하나로 퍼널 전체가 잘린다.
  // `_x` 는 `v` 파라미터 없이 들어온 방문이다. 화면은 fallback(a)으로 그려지지만
  // CRM 링크로 온 게 아니므로 실험군에 섞으면 안 된다.
  "fam_photo_a" = "fam_photo_a",
  "fam_photo_b" = "fam_photo_b",
  "fam_photo_c" = "fam_photo_c",
  "fam_photo_d" = "fam_photo_d",
  "fam_photo_x" = "fam_photo_x",
  "hs" = "hs",
}

export enum RefundServicePathType {
  "refund" = "refund",
  "refund_select_payment_period" = "refund_select_payment_period",
  "refund_select_product" = "refund_select_product",
  "refund_result" = "refund_result",
}

export enum MissParlorEventPathType {
  "prlr_home" = "prlr_home",
  "prlr_fam_main" = "prlr_fam_main",
  "prlr_fam_keyword" = "prlr_fam_keyword",
  "prlr_directions" = "prlr_directions",
  "prlr_region" = "prlr_region",
  "prlr_sido" = "prlr_sido",
  "prlr_facility" = "prlr_facility",
  /** parlor 서브도메인 공통 헤더. 어느 페이지에서 눌렀는지는 page_location 으로 본다 */
  "prlr_cremat" = "prlr_cremat",
  "prlr_header" = "prlr_header",
  "prlr_mubinso" = "prlr_mubinso",
  // 장례식장 임종 안내 랜딩 (parlor.goi/place/[id]/urgent-call).
  // 임종 접수 전화가 주 전환, 비용 리드 시트(home)와 체크리스트 모달(checklist)이 보조.
  // path 11자 -> trigger 예산 22자 (GA4 40자 truncate). i_checklist_* 는 최대 19자라 안전.
  "prlr_urgent" = "prlr_urgent",
}
// 본 PagePath는 가급적 GTM상 uv와 동일한 층위를 가져갑니다.
export enum EventPathType {
  "product_consult_exclusive" = "product_consult_exclusive",
  "gnb" = "gnb", //gnb -> PC 상단 탭, 모바일 하단 공통
  "gdrwr" = "gdrwr", // global drawer
  "footer" = "footer",
  "parlor" = "parlor",
  "parlor_recomm_list" = "parlor_recomm_list",
  "snackbar" = "snackbar",
  "step_guide" = "step_guide",
  "step_guide_parlor" = "step_guide_parlor",
  "parlor_detail" = "parlor_detail",
  "parlor_region" = "parlor_region",
  "parlor_region_service" = "parlor_region_service",
  // 지역 장례식장 fold 카드(C안) 비교·추천 신청 페이지 (/match/region-compare)
  "region_compare" = "region_compare",
  // 지역 장례 맞춤 비용 확인 페이지 (/match/region-cost, 4-way ③)
  "region_cost" = "region_cost",
  // 무빈소 빠르게 알아보기 임베디드 카드 (4-way ①, region/service 임베드)
  "mubinso_quick" = "mubinso_quick",
  // 내 근처 장례식장 빠르게 알아보기 (범용 빠른 폼, region/service 임베드)
  "region_quick" = "region_quick",
  // 지역x서비스 페이지의 FileRequestModal 전용 path.
  // file_request_mdl-marketing_agree/decline이 잘린 후에도 유니크하려면 path가 5자 이하여야 함 (GA4 40자 truncate)
  "rgsvc" = "rgsvc",
  "parlor_detail_request" = "parlor_detail_request",
  "parlor_backlink_list" = "parlor_backlink_list",
  "facility_detail" = "facility_detail",
  "facility_guide" = "facility_guide",
  "facility-detail" = "facility-detail",
  "facility_search" = "facility_search",
  "funeral_facilities" = "funeral_facilities",
  "service" = "service",
  "product" = "product",
  "review_detail" = "review_detail",
  "review" = "review",
  "funeral_prepare" = "funeral_prepare",
  "notice_detail" = "notice_detail",
  "parlor_detail_price" = "parlor_detail_price",
  "parlor_match_request" = "parlor_match_request",
  // 장례식장 비용 안내 리드 퍼널(match/parlor-requests 실험 B안).
  // path를 11자로 짧게 잡아 trigger 예산 22자를 확보했다 (GA4 40자 truncate).
  "parlor_cost" = "parlor_cost",
  "parlor_match_request_passed_away" = "parlor_match_request_passed_away",
  "parlor_only_match_request" = "parlor_only_match_request",
  "facility_region" = "facility_region",
  "facility_match_request" = "facility_match_request",
  "facility_match_recommendation" = "facility_match_recommendation",
  "jangji_done" = "jangji_done",
  "match_request" = "match_request",
  "match_req_leave" = "match_req_leave",
  "parlor_req_quit" = "parlor_req_quit",
  "mubinso_funnel" = "mubinso_funnel",
  "crema_funnel" = "crema_funnel",
  "hubul" = "hubul",
  "beneficiary" = "beneficiary",
  "sangjo_lp" = "sangjo_lp",
  "parlor_mbn" = "parlor_mbn",
  "parlor_cmp" = "parlor_cmp",
  "parlor_fst" = "parlor_fst",
  "facility_prep" = "facility_prep",
  /** www 장례 비용 계산기(/match/cost-calculator/). 가이드북·후기 하단 카드에서 진입 */
  "cost_calc" = "cost_calc",
  /** 장례 비용 카드 실험(#361~#363) A/B 공통 슬롯 클릭. A 는 그 자리 기존 CTA, B 는 카드 */
  "cost_card_ab" = "cost_card_ab",
  "crematorium_match_request" = "crematorium_match_request",
  "match_request_complete" = "match_request_complete",
  "match_recommendation_detail" = "match_recommendation_detail",
  /** 견적 제출 후 대기 화면. match_recommendation_detail(27자)은 트리거가 6자만 남아 새 path로 분리 */
  "quote_pending" = "quote_pending",
  "match_clsd_req" = "match_clsd_req",
  "rlx" = "rlx",
  "mtch_recomm_dtl" = "mtch_recomm_dtl",
  "mtch_recomm_dtl_suggestions" = "mtch_recomm_dtl_suggestions",
  "mtch_recomm_lst" = "mtch_recomm_lst",
  "match" = "match",
  "login" = "login",
  "home" = "home",
  "home_sa_1" = "home_sa_1",
  "home_sa_2" = "home_sa_2",
  "guidebook" = "guidebook",
  "guide" = "guide",
  "guidebook_search" = "guidebook_search",
  "funeral" = "funeral",
  "funeral_detail" = "funeral_detail",
  "funeral_prepare_detail" = "funeral_prepare_detail",
  "consult_list" = "consult_list",
  "my_detail" = "my_detail",
  "my" = "my",
  "my_v3" = "my_v3",
  "my_v2" = "my_v2",
  "my_v2_guide" = "my_v2_guide",
  "gdb_tip" = "gdb_tip",
  "gdb_tip_bfr" = "gdb_tip_bfr",
  "gdb_tip_cvs" = "gdb_tip_cvs",
  "guide_about_parlor_funeral" = "guide_about_parlor_funeral",
  "promotion" = "promotion",
  "guide_content" = "guide_content",
  "match_compare_service" = "match_compare_service",
  "hospice_search" = "hospice_search",
  "hospice_detail" = "hospice_detail",
  "campaign" = "campaign",
  "crematorium_detail" = "crematorium_detail",
  "crematorium_region" = "crematorium_region",
  "crematorium_recommendation" = "crematorium_recommendation",
  "funeral_cost" = "funeral_cost",
  "infrmatin_price_parlor" = "infrmatin_price_parlor",
  "match_facility_request" = "match_facility_request",
  "match_facility_request_crematorium" = "match_facility_request_crematorium",
  "consultant_search" = "consultant_search",
  "directors_parlor" = "directors_parlor",
  "sea_burial" = "sea_burial",
  "sea_burial_region" = "sea_burial_region",
  "sea_burial_detail" = "sea_burial_detail",
  "owned_request" = "owned_request",
  "care" = "care",
  "care_detail" = "care_detail",
  "promotion_parlor_50" = "promotion_parlor_50",
  "match_facil_req_prime" = "match_facil_req_prime",
  "info_price_facil" = "info_price_facil",
  "facility_relocate" = "facility_relocate",
  "premium_2024" = "premium_2024",
  "parlor_estimates" = "parlor_estimates",
  "post_paid_funeral_prepare" = "post_paid_funeral_prepare",
  "funeral_checklist" = "funeral_checklist",
  "product_main" = "product_main",
  "product_packages" = "product_packages",
  "match_recommendation_tips" = "match_recommendation_tips",
  "promotion_bnf" = "promotion_bnf",
  "jangji_promotion" = "jangji_promotion",
  "jangji_prime" = "jangji_prime",
  "jangji_prepare" = "jangji_prepare",
  "jangji_blog" = "jangji_blog",
  "jangji_retention" = "jangji_retention",
  "consult_proposal" = "consult_proposal",
  "consult_proposal2" = "consult_proposal2",
  "msg_hidden_cost" = "msg_hidden_cost",
  "message_last_check" = "message_last_check",
  "cost_standard" = "cost_standard",
  "msg_farewell" = "msg_farewell",
  "msg_cost_grow" = "msg_cost_grow",
  "msg_after7" = "msg_after7",
  "msg_family_decide" = "msg_family_decide",
  "msg_cost_items" = "msg_cost_items",
  "msg_good_funeral" = "msg_good_funeral",
  "msg_prep" = "msg_prep",
  "msg_prep_advance" = "msg_prep_advance",
  "msg_mubinso" = "msg_mubinso",
  "msg_procedure" = "msg_procedure",
  "parlor_ad_quote" = "parlor_ad_quote",
  // now 콜 확대 임종 배너에서 연 문의 유형 모달. 헤더 24시간 버튼 모달(각 페이지 path)과 나눠 본다
  "urgent_bnr" = "urgent_bnr",
  "funeral_cost_ad" = "funeral_cost_ad",
  "nowake_landing" = "nowake_landing",
}

export interface RefundServiceTriggerAndTargetType {
  refund: "landing_CTA" | "footer_CTA";
  refund_select_product: "footer_CTA" | "sangjo_item" | "sangjo_product_item";
  refund_select_payment_period: "footer_CTA";
  refund_result: "footer_left_CTA" | "footer_right_CTA";
}

export interface MissParlorEventTriggerAndTargetType {
  prlr_home:
    | "header-uv_mtch_rqst"
    | "header-goi_call"
    | "hero-uv_mtch_rqst"
    | "region_filter-tab"
    | "search-query"
    | "card-uv_prlr_detail";
  prlr_fam_main: // 장례식장 전화 CTA 에서 뜨는 문의 유형 모달.
  // ask-visit(조문)만 장례식장 직통, 나머지 셋은 고이 1666-9784
  | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    // 상단 "지금 바로 전화주세요" 띠지에서 모달을 연다
    | "hdr_consult"
    | "nav-info"
    | "nav-uv_mtch_rqst_prlr"
    | "nav-fune-proc"
    | "nav-road"
    | "btn-hwahwan"
    | "btn-goi_call"
    | "btn-uv-htch_rqst_prlr"
    | "footer-uv_mtch_rqst_prlr"
    | "uv-mtch_rqst_prlr"
    | "footer_goi_call"
    | "goi_call"
    | "btn_to_directions"
    | "btn_to_mubinso"
    // now 콜 확대 첫 화면 임종 배너 전화 버튼과, 배너가 스크롤로 사라진 뒤 하단 버튼 (parlor_ad_quote 와 같은 이름)
    | "urgent_bnr_call"
    | "urgent_flt_call";
  prlr_fam_keyword: "";
  // 장지 시설 상세 (parlor). path 13자라 trigger 는 20자 안에서 unique 해야 한다
  prlr_facility:
    | "share_kakao"
    | "share_naver"
    | "share_band"
    | "share_mail"
    | "share_copy"
    | "nearby_facility"
    | "nearby_region"
    | "guide_link"
    | "btn_quote"
    | "btn_directions"
    // DirectionsMap 재사용. 장례식장 directions 페이지와 같은 trigger 이름을 쓴다
    | "btn_naver_map"
    | "btn_kakao_map"
    | "tel_link"
    | "nav_section"
    | "fixed_copy_address"
    | "fixed_quote";
  /**
   * 화장장 상세(/crematorium/{id}/).
   *
   * path 를 prlr_crematorium 이 아니라 prlr_cremat 으로 줄인 이유는 GA4 의
   * 이벤트명 40자 제한이다. click-{path}-{trigger} 형식이라 path 가 11자면
   * trigger 는 22자까지 살아남는다. 가장 긴 fixed_copy_address 가 18자다.
   *
   * 앞의 10개는 장지 상세와 공용 컴포넌트(DirectionsMap, FacilityNav,
   * FacilityShareRow, FacilityFixedBar)를 그대로 쓰기 때문에 이름이 같아야 한다.
   */
  prlr_cremat:
    | "share_kakao"
    | "share_naver"
    | "share_band"
    | "share_mail"
    | "share_copy"
    | "nav_section"
    | "fixed_copy_address"
    | "fixed_quote"
    | "btn_naver_map"
    | "btn_kakao_map"
    // 여기부터 화장장 상세 고유
    // btn_consult 는 히어로 주 버튼(고이 상담), btn_call 은 기본 정보 표의
    // 시설 직통 번호다. 대상이 달라서 나눠 둔다
    | "btn_consult"
    // 상단 "지금 바로 전화주세요" 띠지(ParlorHeader). 화장장 상세에서만 모달을 띄운다
    | "hdr_consult"
    // btn_consult, hdr_consult 에서 뜨는 문의 유형 모달. parlor 는 call() 을 안 써서
    // ask-booking / ask-etc 도 raw tel: 로 바로 걸린다(새벽 차단 없음)
    | "ask-booking"
    | "ask-relocation"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    | "btn_call"
    | "btn_quote"
    | "fee_detail"
    | "link_parlor"
    | "link_burial"
    | "link_cremat"
    | "link_region";
  /** 공통 헤더 Nav. 광고 LP 에서는 Nav 자체를 끄므로 발화하지 않는다 */
  prlr_header:
    | "nav_hdr_parlor"
    | "nav_hdr_facility"
    | "nav_hdr_crematorium"
    | "nav_hdr_charnel"
    | "nav_hdr_natural"
    | "nav_hdr_cemetery"
    | "nav_hdr_cost";
  /**
   * 장례식장별 무빈소 장례 안내 페이지(/place/{id}/mubinso/).
   * path 가 12자라 trigger 는 앞 21자가 unique 해야 GA4 40자 truncate 에 안전하다.
   */
  prlr_mubinso: // 장례식장 전화 CTA 에서 뜨는 문의 유형 모달.
  // ask-visit(조문)만 장례식장 직통, 나머지 셋은 고이 1666-9784
  | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    // 상단 "지금 바로 전화주세요" 띠지에서 모달을 연다
    | "hdr_consult"
    | "btn_cta_cost"
    | "btn_cta_process"
    | "btn_call_cost"
    | "btn_call_place"
    | "btn_call_bottom"
    | "btn_crumb_region"
    | "btn_crumb_parlor"
    | "btn_to_parlor"
    | "btn_to_directions"
    | "btn_to_region"
    | "btn_to_service"
    | "btn_to_company"
    | "btn_review"
    | "btn_guide"
    | "i_fixed_checklist"
    | "i_fixed_cost"
    | "i_checklist_open"
    | "i_checklist_submit"
    | "i_checklist_success"
    | "i_checklist_close"
    | "i_checklist_agree"
    | "i_checklist_no"
    | "i_error"
    | "btn_nearby_parlor"
    | "btn_nearby_charnel"
    | "btn_nearby_cremat"
    | "btn_naver_map"
    | "btn_kakao_map";
  // 장례식장 임종 안내 랜딩. path 11자 -> trigger 예산 22자, 아래 전부 19자 이하라 충돌 없다.
  // i_checklist_* 는 DirectionsChecklistModal 이 그대로 발화한다 (directions/mubinso 와 같은 이름).
  prlr_urgent:
    | "uv"
    | "call_header"
    | "call_banner"
    | "call_cta"
    | "cost_sheet_open"
    // v3 하단 링크 CTA("장례 견적 1분만에 받아보기"). 같은 시트지만 진입점이 달라 분리한다
    | "cost_sheet_open2"
    | "cost_sheet_close"
    // v3 하단 "고이상조 서비스 보러가기" (platform /product 로 이탈)
    | "sangjo_link"
    | "cost_submit_try"
    | "cost_submit_ok"
    | "guests-0"
    | "guests-50"
    | "guests-100"
    | "guests-200"
    | "urgency-now"
    | "urgency-week"
    | "urgency-month"
    | "urgency-notyet"
    | "fab_checklist"
    | "i_checklist_open"
    | "i_checklist_submit"
    | "i_checklist_success"
    | "i_checklist_close"
    | "i_checklist_agree"
    | "i_checklist_no"
    // now 콜 확대 첫 화면 임종 배너 전화 버튼과, 배너가 스크롤로 사라진 뒤 하단 버튼 (parlor_ad_quote 와 같은 이름)
    | "urgent_bnr_call"
    | "urgent_flt_call";
  prlr_directions: // 장례식장 전화 CTA 에서 뜨는 문의 유형 모달.
  // ask-visit(조문)만 장례식장 직통, 나머지 셋은 고이 1666-9784
  | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    // 상단 "지금 바로 전화주세요" 띠지에서 모달을 연다
    | "hdr_consult"
    | "i_full"
    | "i_gauge"
    | "i_submit_try"
    | "i_submit"
    | "i_conflict"
    | "i_error"
    | "i_checklist_open"
    | "i_checklist_submit"
    | "i_checklist_success"
    | "i_checklist_close"
    | "i_checklist_agree"
    | "i_checklist_no"
    | "i_fixed_call"
    | "i_fixed_cost"
    | "btn_call"
    | "btn_naver_map"
    | "btn_kakao_map"
    | "btn_to_parlor"
    | "btn_to_mubinso"
    | "btn_cta_lead_middle"
    | "btn_cta_lead_bottom"
    | "btn_cta_lead_fixed_estimate"
    | "btn_cta_lead_fixed_call"
    | "btn_nearby_directions"
    | "section_view_transit"
    | "section_view_driving"
    | "section_view_amenities";
  /**
   * 지역 장례식장 리스트 페이지(/region/{시도}/{시군구}).
   * path가 11자라 trigger는 앞 22자가 unique해야 GA4 40자 truncate에 안전하다.
   */
  prlr_region:
    | "nav_parlor"
    | "nav_crematorium"
    | "nav_burial"
    | "nav_faq"
    | "sort_price"
    | "sort_binso"
    | "filter_all"
    | "filter_public"
    | "filter_parking"
    /** 시도 페이지의 지역(시군구) 필터 */
    | "filter_sigungu"
    /** 장지 목록 필터. 운영형태와 유형을 한 줄에 섞은 단일 선택 */
    | "filter_burial_all"
    | "filter_burial_public"
    | "filter_burial_private"
    | "filter_burial_charnel"
    | "filter_burial_natural"
    | "filter_burial_cemetery"
    | "btn_more"
    | "btn_fold"
    | "btn_call"
    | "btn_total_cost"
    | "hero_total_cost"
    | "fixed_cost"
    | "fixed_call"
    | `btn_parlor_quote_${number}`
    | `faq_open_${number}`
    | "link_nearby"
    /** 장지 섹션 하단의 인근 지역 장지 링크 */
    | "link_nearby_facility"
    /** 전국 인덱스(/region/parlor/, /region/facility/)의 시도 카드와 반대쪽 인덱스 */
    | "link_sido"
    | "link_other_index"
    /** 전국 인덱스의 지역 검색 입력 (blur 시 1회) */
    | "search_region"
    | "link_parlor_detail"
    | "crumb_sido"
    | "link_crematorium"
    | "link_burial"
    | "crematorium_terms"
    | "link_crematorium_all";
  /** 시도 허브 페이지(/region/{시도}). 시군구 인덱스 역할 */
  prlr_sido:
    | "btn_call"
    | "hero_total_cost"
    | "link_sigungu"
    | "link_other_sido";
}

export interface DirectEventTriggerAndTargetType {
  gnb:
    | "call_modal_CTA"
    | "call_modal_CTA2"
    | "call_modal_faq"
    | "call_modal_consult"
    | "uv_home"
    | "uv_premium"
    | "uv_product_list"
    | "menu_close"
    | "diff_sangjo_compare"
    | "uv_b2b"
    | "uv_intro"
    | "uv_content"
    | "uv_faq"
    | "contract_check"
    | "tab_all"
    | "tab_funeral"
    | "tab_service"
    | "tab_flower";
  prepare_content:
    | "prepare_1"
    | "prepare_2"
    | "prepare_3"
    | "share"
    | "before_content"
    | "next_content"
    | "go_to_home"
    | "btn_consult"
    | "btn_100won"
    | "article_sangjo";
  contract_check:
    | "cancel_m990_contract"
    | "slct_cncl_contract"
    | "img_cncl_contract"
    | "slct_memo_cncl_contract"
    | "back_cncl_contract"
    | "slct_memo_cncl_1"
    | "slct_memo_cncl_2"
    | "slct_memo_cncl_2_1"
    | "slct_memo_cncl_2_2"
    | "slct_memo_cncl_2_3"
    | "slct_memo_cncl_2_4"
    | "slct_memo_cncl_3"
    | "submit_cncl_contract"
    | "CTA-about_100_payment"
    | "CTA-match_request"
    | "CTA-additional_contract"
    | "CTA-consult-match_request"
    | "btn_pay_deferral_defer"
    | "btn_pay_deferral_cancel"
    | "btn_pay_suspend_confirm"
    | "btn_pay_suspend_cancel"
    | "jangji_banner_click"
    | "keep1_accept"
    | "keep1_reject"
    | "single_keep"
    | "single_cncl"
    | "final_keep"
    | "final_cncl"
    // 가입 정보 변경 모달 버튼. click-contract_check- 가 21자라 트리거는 19자 이하.
    | "btn_chg_payment"
    | "btn_cncl_contract"
    | "CTA_verify"
    | "slct_urgency"
    | "slct_urgency_1"
    | "slct_urgency_2"
    | "slct_urgency_3"
    | "urgency_edit_open"
    | "urgency_edit_confirm"
    // 임박 온보딩(/contract-check/onboarding). PostSignupFunnel 의 캐러셀·동의
    // 시트·완료 모달을 공유하므로 트리거 이름을 registration 과 동일하게 두고
    // path 로만 진입을 구분한다. 같은 트리거를 진입별로 직접 비교할 수 있다.
    // ⚠️ 슬라이드가 10장을 넘기면 psf_carousel_next_10 이 20자가 되어
    //    click-contract_check-psf_carousel_next_1 로 truncate 되며 _1 과 충돌한다.
    //    현재 URGENT_SLIDES 는 4장.
    | `psf_carousel_next_${number}`
    | `psf_carousel_prev_${number}`
    | "psf_agree_open"
    | "psf_agree_all"
    | "psf_complete_ok"
    | "psf_save_contact"
    // 견적 제출 성공. 결제 후 온보딩은 prime_request path 로 같은 trigger 를
    // 쏘므로 path 만 달라 두 진입의 최종 전환을 나눠 볼 수 있다.
    | "submit_success"
    | "article_click"
    | "ms99_bnf_review"
    | "ms99_bnf_cncl"
    | "ms99_bnf_banner"
    // 100원 가입자용 계약서 혜택 배너. ms99_bnf_banner 와 나란히 진입 경로를 나눠 본다
    | "ms100_bnf_banner";
  benefit: "benefit_CTA";
  home:
    | "home_promotion-9900"
    | "hero_CTA-uv_registration"
    | "fixed_CTA"
    | "section_CTA"
    | "uv_pr"
    | `goi_detail-uv_link_${number}`
    | "card-uv_review_detail"
    | "consultant_CTA"
    | "benefit_banner_close"
    | "benefit_banner_click"
    | "memorialtable_banner_click"
    | "jangji_banner_click"
    | "uv_about-promise"
    | "uv_detail"
    | "uv_service"
    | "memorialtable_detail-uv_memorialtable"
    | "scroll_10"
    | "scroll_25"
    | "scroll_50"
    | "scroll_75"
    | "scroll_95"
    | "quiz_section_click"
    | "google_CTA-uv_call_lead"
    | "hero_btn_quiz"
    | "btn_revisit"
    | "facility-uv_list";
  readiness_quiz:
    | `q${1 | 2 | 3}_answer`
    | "complete_high"
    | "complete_low"
    | "cta_cost"
    | "cta_consult"
    | "consult_submit";
  saving: "";
  toss: "info_CTA";
  home_honam: "";
  home_dggb: "";
  feed: "" | "btn_answer" | "btn_cta";
  comparison: "";
  closed: "";
  sangjo_join: "" | "btn_consult" | "btn_100won" | "article_prep3";
  call_lead:
    | "submit_call_lead"
    | "submit_consult_mdl_call_lead"
    | "submit_consult_btm_call_lead"
    | "submit_payment_fail_consult_btm_call_lead"
    | "open_consult_btm_call_lead"
    | "open_payment_fail_consult_btm"
    | "open_consult_btm_100_method_call_lead"
    | "open_consult_btm_method_selection_call_lead"
    | "close_consult_btm_call_lead"
    | "close_payment_fail_consult_btm"
    | "google_fab_consult"
    | "consult_mdl_open"
    | "open_consult_btm_verification"
    | "submit_consult_btm_verification"
    | "fab_click_benefit"
    | "fab_click_service"
    | "fab_click_compare"
    | "fab_click_review"
    | "consult_mdl_close"
    | "select_consult_mdl_option"
    | "select_consult_btm_option"
    | "agree_marketing_consent"
    | "click_reason_next"
    | "click_route_leave_cta"
    | "direct_call_click";
  content:
    | "tab_funeral"
    | "tab_review"
    | "tab_all"
    | "tab_youtube"
    | "tab_feed"
    | "uv_sn-1"
    | "uv_sn-2"
    | "youtube_1"
    | "youtube_2"
    | "youtube_3"
    | "sn-2-uv_sn-1"
    | "sn-1-uv_sn-2"
    | "caring_cta_consult"
    | "caring_submit_consult"
    | "CTA-uv_match_request"
    | "filter_100won"
    | "filter_good_sangjo"
    | "filter_funeral_prep"
    | "filter_funeral_now"
    | "btn_all_content"
    | "btn_more_content"
    | `filter_tag_${string}`
    | `uv_feed-${string}`
    | `uv_article_${string}`;
  gasapp: "btn_participate" | "CTA";
  content_sn_1: "";
  content_sn_2: "";
  home_female: "";
  product_list: "uv_detail" | `uv_detail_${string}`;
  product_detail:
    | `opn_mdl`
    | "tp_timer_CTA"
    | "bt_timer_CTA"
    | "fixed_CTA"
    | `send_email`
    | "mdl_submit_btn"
    | `mdl_name_inpt`
    | `mdl_phone_inpt`
    | `mdl_all_chk`
    | `mdl_term_chk`
    | `mdl_prsnl_chk`
    | "mdl_CTA"
    | "mdal_parlor50_CTA";
  calculator:
    | "intro_CTA"
    | "1_CTA"
    | "2_CTA"
    | "2_back"
    | "3_CTA"
    | "3_toggle_on"
    | "3_toggle_off"
    | "3_share"
    | "3_share_complete";
  faq: "CTA_call" | "CTA_uv_home";
  faq_detail:
    | "CTA_call_detail"
    // 가입 고객 문의 상세. 전화 버튼은 _call 로 끝난다. click-faq_detail- 가 17자라 트리거는 23자 이하.
    | "CTA_member_terminate"
    | "CTA_member_refund_call"
    | "CTA_member_account"
    | "CTA_member_payment"
    | "CTA_member_unpaid"
    | "CTA_member_contract"
    | "CTA_member_paper_call"
    | "CTA_member_info_call";
  registration:
    | "toss-regular_verification"
    | `success-additional-${string}`
    | "trust_banner_click"
    | "payment_success_uv_match_request"
    | "toggle_mode_switch"
    | "btmsht_account_chip"
    | `btmsht_account_chip_${1 | 2 | 3 | 4 | 5}`
    | `btmsht_account_chip_${number}`
    | "btmsht_over_account_chip"
    | `btmsht_account_select_${
        | "mother"
        | "father"
        | "motherInLaw"
        | "fatherInLaw"
        | "extra"}`
    | "all_required_agree_btn"
    | "all_optional_agree_btn"
    | "all_information_info_CTA"
    | "check_product_CTA"
    | "account_select_CTA"
    | "verification_CTA"
    | "verification_btm_CTA"
    | "open_verification_btm"
    | "address_CTA"
    | "name_info_CTA"
    | "phone_info_CTA"
    | "birthdate_info_CTA"
    | "gender_info_CTA"
    | "user_info_CTA"
    | "contract_CTA"
    | "email_mdl-close"
    | "opn_contract_mdl"
    | "opn_contract_chk_list_mdl"
    | "CTA-uv_home"
    | "retry_payment"
    | "product_intro_cta"
    | `product_intro_step-${number}`
    | "contract_intro_cta"
    | `contract_intro_step-${number}`
    | "trust_intro_cta"
    | `trust_intro_step-${number}`
    | "trust_intro_consult"
    | `plan_intro_select_${number}`
    | "plan_intro_cta"
    | `plan_intro_cta_${number}`
    | `plan_intro_account_${number}`
    | "plan_confirm_proceed"
    | "open_continue_mdl"
    | "continue_registration"
    | "restart_registration"
    | "payment_success"
    | "fomo_returning_payment_success"
    | "accounts_minus_one"
    | "accounts_plus_one"
    | "accounts_plus_two"
    | "over_accounts_CTA"
    | "failure_CTA"
    | `pay_slct-${string}`
    | `pay_slct-cycle_${string}`
    | `pay_slct-prd_${string}`
    | `payment_contract_check_opn`
    | "payment_product_opn"
    | "prime_payment_opn"
    | "payment_cycle_opn"
    | "prime_payment_submit"
    | "payment_account_opn"
    | "payment_attempt_toss"
    | "payment_attempt_easypay"
    | "payment_attempt_hectopay"
    | "payment_completed_toss"
    | "payment_completed_easypay"
    | "payment_completed_hectopay"
    | "success-uv_service"
    | "success-premium_modal"
    | "payment_completed_product_100"
    | "payment_completed_product_9900"
    | "payment_completed_product_19800"
    | `payment_completed_product_${number}`
    | `tutorial_step-${number}`
    | `tutorial_prev_${number}`
    | "payment_completed_product_30000"
    | "payment_contract-next_product_1"
    | "go_pay_bfr_account"
    | "go_pay_bfr_pay_slct"
    | "info_tab_trust"
    | "info_tab_product"
    | "all_agree_btn"
    | "all_agree_btn_top"
    | "all_agree_btn_bottom"
    | "go_FAQ"
    | "submit_survey"
    | `uv_feed-${string}`
    | "100_payment_submit"
    | "saving_payment_submit"
    | `saving_payment_submit_${"card" | "account"}`
    | "close_30000_induce_modal"
    | "close_9900_induce_modal"
    | "select_100_from_induce_modal"
    | "select_30000_from_induce_modal"
    | "select_9900_from_induce_modal"
    | "uv_100_pay_sheet"
    | "uv_saving_pay_sheet"
    | "uv_saving_pay_card_only_sheet"
    | "uv_saving_pay_no_discount_sheet"
    | "info_CTA_top"
    | "CTA-refund_select"
    | "next_survey"
    | "verification_pass"
    | "verification_eziok"
    | "prevent_change"
    | "other_payment_modal_open"
    | "submit_other_payment"
    | "verifi_nice_CTA"
    | "verifi_nice_name"
    | "verifi_nice_rrn"
    | "verifi_nice_carrier"
    | "verifi_nice_sms_CTA"
    | "verification_manual_CTA"
    | "easypay_card_type_open"
    | "easypay_card_type_select"
    | "easypay_card_input_complete"
    | "easypay_agreement_submit"
    | "submit_easypay_card_payment"
    | "easypay_help_btn_open"
    | "easypay_help_30000_click"
    | "easypay_help_consult_click"
    | "easypay_consult_submit"
    | "uv_account_guide_sheet"
    | "account_guide_confirm"
    | "account_guide_back"
    | "trust_ctnts_banner"
    | "trust_btm_close"
    | "trust_btm_confirm"
    | "trust_btm_ok"
    | "trust_btm_consult"
    | `trust_btm_next_${number}`
    | `trust_btm_prev_${number}`
    | "psf_complete_next"
    | `psf_carousel_next_${number}`
    | `psf_carousel_prev_${number}`
    | "psf_agree_open"
    | "psf_agree_all"
    | "psf_complete_ok"
    | "psf_save_contact"
    | "psf_content_cta"
    | "psf_benefit_cta"
    | "psf_contract_cta";
  b2b:
    | "hero_CTA-opn_mdl"
    | "hero_CTA-call"
    | "mdl_cmpnyname_inpt"
    | "mdl_email_inpt"
    | "biz_lite_check"
    | "biz_standard_check"
    | "biz_premium_check"
    | "biz_etc_check";
  introduce: "";
  introduce_revisit: "CTA" | "review_detail";
  introduce_quiz:
    | "btn_start"
    | "btn_back"
    | `q${1 | 2 | 3}_answer`
    | `q${1 | 2 | 3}_next`
    | `q${1 | 2 | 3}_cta`
    | "summary_cta";
  coupon: "";
  half: "";
  content_care: "";
  prime_request:
    | "status-answer"
    | `status-answer-${number}`
    | "sido-answer"
    | "condolers-answer"
    | "submit_success"
    | "tab_checklist"
    | "intro_CTA"
    | "agreement_all"
    | "agreement_privacy"
    | "agreement_emergency"
    | `next_step-${number}`;
  paybk: "";
  appintoss: `coupon_generate_${"click" | "success"}`;
  calculator_daangn:
    | "next_to_step2"
    | "submit_call_lead"
    | "edit_region"
    | `progress_quiz_q${number}_answer_${"o" | "x"}`
    | `progress_quiz_next_q${number}`
    | "progress_quiz_complete"
    | "progress_view_result";
  calculator_caring:
    | "next_to_step2"
    | "submit_call_lead"
    | "edit_region"
    | `progress_quiz_q${number}_answer_${"o" | "x"}`
    | `progress_quiz_next_q${number}`
    | "progress_quiz_complete"
    | "progress_view_result";
  referral: "page_view" | "btn_share" | "friend_visit" | "friend_signup";
  lead_leadform:
    | "intro_cta"
    | "q1_select"
    | "q2_select"
    | "open_consult_btm"
    | "close_consult_btm"
    | "submit_call_lead"
    | "complete_close"
    | "complete_explore";
  contract_oxquiz: `q${1 | 2 | 3}_answer` | "btn_cta";
  /**
   * 겨울 효도여행 랜딩. 숙소 카드 클릭만 쏜다(진입은 GA4 page_view 로 센다).
   * 이름 길이: 가장 긴 `click-lp_trip_winter-btn_andong_grand` 가 37자라 40자 안이다.
   */
  lp_trip_winter:
    | "btn_parkroche"
    | "btn_seamarq"
    | "btn_yeonhwamun"
    | "btn_stanford"
    | "btn_cheongpung"
    | "btn_lagung"
    | "btn_andong_grand"
    | "btn_eden";
  /**
   * 겨울 케어 선물 랜딩. 상품 카드 클릭만 쏜다(진입은 GA4 page_view 로 센다).
   * 이름 길이: 가장 긴 `click-lp_winter_care_gift-btn_m_heatspot` 가 40자라 한도 안이다.
   */
  lp_winter_care_gift:
    | "btn_m_waist"
    | "btn_m_neck"
    | "btn_m_eye"
    | "btn_m_wrist"
    | "btn_m_ems"
    | "btn_m_calf"
    | "btn_m_slipper"
    | "btn_f_citrus"
    | "btn_f_honey"
    | "btn_f_tea"
    | "btn_f_ginseng"
    | "btn_f_apple"
    | "btn_b_blanket"
    | "btn_b_mist"
    | "btn_b_humid1"
    | "btn_b_humid2"
    // 예비 상품 (교체되면 노출. 미리 등록해 두면 교체 때 이 파일을 고치지 않아도 된다)
    | "btn_m_heatspot"
    | "btn_m_mini"
    | "btn_m_cpillow"
    | "btn_f_gotgam"
    | "btn_f_tea10"
    | "btn_f_tea20"
    | "btn_f_apple4"
    | "btn_b_warmer"
    // 2026-11-03 후보 재조사로 추가한 예비
    | "btn_f_osulloc"
    | "btn_f_hcafe"
    | "btn_b_humid3"
    | "btn_b_heatpad";
  prime_benefit:
    | "itm_fnrl_fac"
    | "itm_mem_tbl"
    | "itm_overseas"
    | "itm_cruise"
    | "itm_pet"
    | "itm_photo"
    | "itm_health"
    | "detail_CTA"
    | "lead_submit"
    | "lead_close"
    | "oth_fnrl_fac"
    | "oth_mem_tbl"
    | "oth_overseas"
    | "oth_cruise"
    | "oth_pet"
    | "cpn_open"
    | "cpn_submit"
    | "cpn_close"
    | "cpn_success"
    | "cpn_register"
    | "cpn_copy"
    | "detail_back"
    /*
     * 반려동물 혜택 신청 퍼널. `click-prime_benefit-pet_%` 하나로 잘린다.
     * 2026-10 상담 신청 → 21그램 키트 사전예약(3단계 위저드)으로 바뀌었고 이름은 유지한다.
     * ⚠️ 이때 세는 기준도 바뀌었다. 상담 시절 pet_submit·lead_submit 은 가입 판정 전에 쏴서
     * 비회원 제출까지 셌고, 사전예약은 /apply 가 접수(ok)한 건만 센다. 전환 전후 수치를
     * 그대로 이어 비교하지 말 것.
     *
     * path 를 따로 파지 않는 이유: 가족사진(fam_photo_*)이 전용 path 를 쓰는 건
     * variant 를 path 에 넣어야 해서고, 펫은 랜딩 A/B 가 있어도 GA 유입값(v·utm)으로
     * 나눈다. 이 흐름은 prime-benefit 여정의 일부라 같은 path 에 둔다.
     *
     * 이벤트를 늘리지 않는 이유: 신청 건은 신청 DB(promo_applications)가 더
     * 정확하고(이벤트는 유실·차단이 있다), 단계 이탈은 `/apply?step=` page_view 로 나온다.
     * 탈락(비회원·중복·마감)은 세지 않기로 했다.
     *
     * pet_submit 은 공통 `lead_submit` 과 **같은 자리에서 함께** 발화한다.
     * lead_submit 은 혜택 신청 총량의 공통 축이라(저장 테이블은 혜택마다 다르다),
     * 빼면 GA 로 총 신청 수를 셀 때 펫만 누락된다. 랜딩 CTA 의
     * detail_CTA + pet_cta 와 같은 구조다. pet_call 은 상담 신청 시절 값으로 더 발화하지 않는다.
     */
    | "pet_cta"
    | "pet_submit"
    | "pet_call"
    /*
     * 혜택 목록이 어떤 view 로 열렸는지(가입 상품 판정 결과). 클릭이 아니라 노출이다.
     * 9,900원·100원 view 는 URL 이 같아 page_view 로 구분되지 않는다.
     * 미가입은 /prime-benefit/not-subscribed page_view 로 나온다
     */
    | "view_9900"
    | "view_100"
    // 미가입자 안내 페이지 "가입 상품 보러가기"
    | "nosub_cta";
  family_share: "btn_copy_link" | "btn_signup" | "btn_review";
  // 랜딩 5버전이 같은 목록을 쓴다. 하나만 바뀌면 variant 간 비교가 깨지므로
  // 개별 나열이 아니라 FamilyPhotoTrigger 한 곳에서 정의한다.
  fam_photo_a: FamilyPhotoTrigger;
  fam_photo_b: FamilyPhotoTrigger;
  fam_photo_c: FamilyPhotoTrigger;
  fam_photo_d: FamilyPhotoTrigger;
  fam_photo_x: FamilyPhotoTrigger;
  hs: "redirect";
}

/**
 * 가족사진 이벤트(시그널스튜디오) trigger.
 *
 * variant 는 path 에 있으므로 여기엔 없다. `properties` 도 쓰지 않는다 —
 * GTM 에 매핑되지 않은 커스텀 파라미터는 BigQuery 까지 가지 않기 때문이다.
 * 그래서 분해가 필요한 축(variant, 완료화면의 apply/story)은 전부 이름에 있다.
 *
 * 분모가 되는 이벤트(`v_*`)를 따로 두는 이유: `/apply/`, `/story/` URL 에는
 * variant 쿼리가 없어서 GA4 page_view 로는 랜딩별 폼 노출을 셀 수 없다.
 */
export type FamilyPhotoTrigger =
  // 노출 — 전환율의 분모
  | "v_land"
  | "v_ap"
  | "v_st"
  // 랜딩 스크롤 깊이 (기존 home 랜딩과 같은 이름 규칙)
  | "scroll_10"
  | "scroll_25"
  | "scroll_50"
  | "scroll_75"
  | "scroll_95"
  // 랜딩 인터랙션
  | "cta_open"
  | "mdl_apply"
  | "mdl_story"
  | "mdl_close"
  | "lst_apply"
  | "lst_story"
  // 이벤트1 — focus(작성 시작) / submit(시도) / done(성공) / dup(중복)
  | "ap_focus"
  | "ap_submit"
  | "ap_done"
  | "ap_dup"
  // 이벤트2 — f(문항 focus) 와 q(다음 클릭) 사이가 "쓰다 포기" 구간이다.
  // 문항3 의 버튼은 "응모 완료"라 q3 가 아니라 st_submit 이다
  | "st_start"
  | "st_dup"
  | "st_f1"
  | "st_q1"
  | "st_f2"
  | "st_q2"
  | "st_f3"
  | "st_submit"
  | "st_done"
  // 완료 화면 — 어느 이벤트에서 왔는지를 이름에 넣는다(properties 미사용)
  | "cp_ap_cross"
  | "cp_ap_fee"
  | "cp_ap_land"
  | "cp_st_cross"
  | "cp_st_fee"
  | "cp_st_land";

// 개별 버튼 텍스트를 이곳에 다 넣어주세요 ^^
// 버튼텍스트_가는곳 - 가는 곳이 uv에 해당하면 무조건 동일하게 이름 넣기. 다른 경우 맞춰서 (모달오픈, 분기 등등)
export interface EventTriggerAndTargetType {
  product_packages: "mubinso-call";
  product_main:
    | "packages_simple"
    | "packages_family"
    | "packages_general"
    | "packages_premium"
    | "card_100won"
    | "card_prime279"
    | "card_prime379"
    | "card_first3"
    | "card_first10"
    | "card_first30"
    | "product_packages"
    | "product_monthly"
    | "product_downpayment"
    | "product-uv_match"
    | "banner-uv_prime"
    | "banner-uv_match_request"
    | "banner-uv_first_package"
    | "banner-uv_memorialtable";
  product_consult_exclusive:
    | "PAYBK279V9"
    | "PAYBK279V10"
    | "PAYBK279V11"
    | "service"
    | "PRIME279S1";
  funeral_checklist:
    | "floating-share"
    | "parlor_button-match_request"
    | "service_button-service"
    | "review_link-review"
    | "cost_button-match_request"
    | "bugo_button-bugo";
  crematorium_match_request:
    | "first-answer"
    | "banner_CTA-uv_crema_match_request";
  match_clsd_req: "";
  match_facil_req_prime: "" | "jangji_intro-next";
  care_detail: // "우리 지역 장례 비용" 카드(LocalCostCard) 클릭 → www 장례 비용 계산기 (실험 #361~#363)
  | "costcard-calc"
    | ""
    | "memorialtable_banner_click"
    | "jangji_banner_click"
    | "banner_blog-uv_blog"
    | "nowake_cta_trend"
    | "nowake_cta_emotion"
    | "nowake_cta_practical";
  care: "card-uv_detail" | "tab_checklist";
  step_guide_parlor: "";
  infrmatin_price_parlor: "";
  my: "";
  my_v3:
    | "back"
    | "cs_call"
    | "kakao"
    | "cost_detail"
    | "new_quote"
    | "funeral_cta"
    | "burial_cta"
    | "burial_agree_all"
    | "burial_sheet_x"
    | "burial_quote"
    | "contract"
    | "contract_ok"
    | "contract_x"
    | "product_diff"
    | "product_ok"
    | "product_x"
    | "cost_gap"
    | "content_more"
    | "content_day2"
    | "content_day5"
    | "content_day7"
    | "parlor_pick"
    | "parlor_toggle"
    | "parlor_close"
    | "parlor_info"
    | "parlor_info_ok"
    | "parlor_info_x"
    | "parlor_cta"
    | "branch_cost_ready"
    | "branch_cost_none"
    | "branch_cost_part"
    | "branch_sub_has"
    | "branch_sub_ease"
    | "branch_sub_urgent"
    | "branch_sub_welfare"
    | "prime_cta"
    | "member_cta"
    | "welfare_cta"
    | `onb_next_${number}`
    | `onb_prev_${number}`
    | "onb_back"
    | "onb_agree_open"
    | "onb_agree_all"
    | "onb_sheet_x"
    | "onb_quote"
    | "onb_save"
    | "onb_home"
    | "logout"
    | "signout";
  my_v2: // 단계 전환 탭
  | "tab_stage-a"
    | "tab_stage-b"
    | "tab_stage-c"
    // A단계
    | "stage-a_acc_cost"
    | "stage-a_acc_consult"
    | "stage-a_acc_philosophy"
    | "stage-a_cta_guide-curious"
    // B단계
    | "stage-b_cost_cta-service"
    | "stage-b_acc_cost"
    | "stage-b_next-steps"
    | "stage-b_cta_guide-compare"
    | "stage-b_cta_guide-hesitating"
    // C단계
    | "stage-c_btn_emg-call"
    | "stage-c_btn_emg-save"
    | "stage-c_mt_btn_join"
    | "stage-c_mt_btn_inquiry"
    | "stage-c_mt_btn_detail"
    | "stage-c_pd_btn_join"
    | "stage-c_first-step_call"
    | "stage-c_guide_funeral-process"
    | "stage-c_guide_obituary"
    | "stage-c_guide_admin-after"
    | "stage-c_btn_review-prev"
    | "stage-c_cta_guide-hesitating"
    // 단계 이동
    | `pager_prev_${number}`
    | `pager_next_${number}`
    // 공통
    | "sticky_btn_call"
    | "ftr_btn_call"
    | "ftr_btn_email"
    | "ftr_btn_kakao"
    // 비로그인
    | "signup_kakao_login";
  my_v2_guide:
    | "curious_cta_call"
    | "curious_cta_kakao"
    | "curious_cta_estimate"
    | "curious_cta_review"
    | "hesitating_cta_call"
    | "hesitating_cta_kakao"
    | "hesitating_link_estimate"
    | "consultation_cta_call"
    | "consultation_cta_kakao"
    | "consultation_cta_estimate";
  funeral_cost:
    | "CTA_footer-uv_match_request"
    | "price-uv_company_introduction"
    | "how_goi-uv_company_introduction"
    | "no_more_cost-uv_company_introduction"
    | "CTA_under-uv_match_request"
    | "CTA_price_detail-uv_match_request"
    | "uv_review"
    | "uv_match";
  parlor:
    | "CTA-uv_match_request"
    | "CTA-uv_match_request_top"
    | "CTA-uv_match_request_bottom";
  parlor_backlink_list: "";
  guide: "";
  // "wooribugo";
  gnb:
    | "report"
    | "hbg_srvc_rcmms-uv_funeral_prepare_1"
    | "contract_check-prime"
    | "signup_modal_close"
    | "open_file_request_modal"
    | "recent_review-uv_review_detail"
    | "content-uv_prepare_1"
    | "content-uv_how_about_parlor_funeral"
    | "content-uv_about_parlor_funeral"
    | "content-uv_prepare_2"
    | "content-uv_company_introduction"
    | "content-uv_funeral_cost"
    | "srvc_rcmms-uv_funeral_prepare_1"
    | "my_consult_list-uv_consult_list"
    | "funeral_info-uv_guidebook"
    | "mypage-uv_my"
    | "mypage-uv_my_v2"
    | "mypage-uv_my_v3"
    | "mtch_rcmms"
    | "logout-trigger_logout"
    | "login-uv_login"
    | "logo-uv_home"
    | "home-uv_home"
    | "funeral-uv_funeral"
    | "review-uv_review"
    | "funeral_or_review"
    | "srvc_rcmms-uv_match_request"
    | "prepare_package-uv_p_p"
    | "discount_btn-uv_p_p"
    | "call-opn_call_mdl"
    | "drawer-mtch_rqst"
    | "drawer-parlor"
    | "drawer-facilities"
    | "drawer-guidebook"
    | "drawer-my"
    | "drawer-service"
    | "drawer-review"
    | "sa_2_service"
    | "sa_2_call_mdl"
    | "hamburger"
    | "funeral-prepare-1"
    | "step-guide"
    | "nav-uv_home"
    | "nav-uv_match_request"
    | "nav-uv_guidebook"
    | "nav-uv_prime_event"
    | "nav-uv_prime"
    | "nav-uv_facility"
    // 장지 상세(/facilities/[id]) 전용 3메뉴. 전역 GNB 4메뉴를 이 페이지에서만 교체한다.
    | "fac_nav-uv_match_cost"
    | "fac_nav-uv_facility_req"
    | "fac_nav-uv_mubinso";
  gdrwr:
    | "main-prime"
    | "main-match_request"
    | "main-facility"
    | "main-review"
    | "main-call"
    | "opn_qttn_rqst-urgent_call"
    | "opn_qttn_rqst-call"
    | "opn_qttn_rqst-keep_quotation"
    | "qttn_rqst-close_urgent_call"
    | "qttn_rqst-close_call"
    | "qttn_rqst-close_keep_quotation"
    | "qttn_rqst-crm_urgent_call"
    | "qttn_rqst-crm_call"
    | "qttn_rqst-crm_keep_quotation";
  footer:
    | "blog_icon-blog"
    | "youtube_icon-youtube"
    | "instagram_icon-instagram"
    | "naver_post_icon-post"
    | "mypage-uv_my"
    | "cs_center-uv_my"
    | "footer-uv_consultant-search"
    | "blog_txt-uv_blog";
  snackbar:
    | "quickcall_mdl-close"
    | "quickcall_mdl-urgent_call-close"
    | "quickcall_mdl-call-close"
    | "ftr_btn_lft-opn_mdl"
    | "ftr_btn_rght-opn_mdl"
    | "urgent_mdl-close"
    | "urgent_mdl_ftr_rght-actn_call"
    | "urgent_mdl_ftr_lft-uv_review"
    | "quick_mdl_ftr_rght-actn_call"
    | "quick_mdl_ftr-urgent_call-one_actn_call"
    | "quick_mdl_ftr-call-one_actn_call"
    | "quick_mdl_ftr_lft-uv_review"
    | "ask_goi-call_goi"
    | "quick_mdl_accordion-opn_dtl"
    | "quick_mdl_accordion-urgent_call-opn_dtl"
    | "quick_mdl_accordion-call-opn_dtl";
  step_guide:
    | "price_compare-uv_match_request"
    | "detail_price-uv_match_request"
    | "initiation-next"
    | "initiation-select_important_factor"
    | "mubinso-match_request_CTA"
    | "mubinso-match_request_fixed"
    | "mubinso-uv_guidebook_mubinso"
    | "mubinso-uv_guidebook_jangji"
    | "yubinso-match_request_CTA"
    | "yubinso-match_request_fixed"
    | "yubinso-uv_review"
    | "yubinso-uv_guidebook_jangji"
    | "onedaybinso-call_fixed"
    | "onedaybinso-call_CTA"
    | "onedaybinso-uv_guidebook_mubinso"
    | "onedaybinso-uv_guidebook_jangji"
    | "prepaid_question-chosun_ilbo"
    | "prepaid_question-ftc_my_sangjo"
    | "prepaid_question-uv_match_request"
    | "prepaid_question-next"
    | "prepaid_question-select_answer"
    | "fcilty_price-uv_stp_gd_dstnce"
    | "fcilty_price-uv_stp_gd_place"
    | "fcilty_price-uv_mtch_rqst"
    | "fcilty_dstnce-uv_stp_gd_price"
    | "fcilty_dstnce-uv_stp_gd_place"
    | "fcilty_dstnce-uv_mtch_rqst"
    | "fcilty_place-uv_stp_gd_price"
    | "fcilty_place-uv_stp_gd_dstnce"
    | "fcilty_place-uv_mtch_rqst";
  parlor_region_service:
    | "goi_call"
    | "seo_top_list"
    | "seo_faq_tgl"
    | "cta_info-file_request"
    | "cta_checklist-file_req"
    | "other_svc-uv_svc"
    | "sido_svc-uv_sido_svc";
  rgsvc:
    | "file_request_mdl-try_submit"
    | "file_request_mdl-submit_success"
    | "file_request_mdl-marketing_agree"
    | "file_request_mdl-marketing_decline"
    | "file_request_mdl-close"
    | "file_request_mdl-info_opt";
  parlor_region:
    | "modal_discount_parlor-close"
    | "modal_discount_parlor-uv_parlor_detail"
    | "list-tab_list"
    | "check_discount-open_modal"
    | "service_by_chip-uv_parlor_region_service"
    | "tab_recomm-tab"
    | "tab_parlor_list-tab"
    | "check_discount-uv_match_request"
    | "CTA_btm_total_price-uv_match_request"
    | "CTA_btm_consult-uv_match_request"
    | "CTA_top-uv_match_request"
    | "parlor_item-uv_parlor_detail"
    | "select_sigungu-select"
    | "sigungu_parlors-uv_sigungu_parlors"
    | "sido_parlors-uv_sido_parlors"
    | "brdcrmb-uv_mtch_recomms"
    | "brdcrmb-uv_parlor_region_service"
    | "brdcrmb-uv_sido_parlors"
    | "brdcrmb-uv_sigungu_parlors"
    | "banner_CTA-uv_step_guide"
    | "sigungu_parlors-uv_sido_parlors"
    | "cost_form_accordion-toggle"
    | "sigungu_chip-uv_sigungu_parlors"
    | "content-uv_gdb_tip_before_4"
    | "sgg_chip_svc-uv_sgg_svc"
    | "intent_now-opn_mdl"
    | "intent_cmp-uv_mtch_rqst"
    | "list_more-scroll_list"
    | "list_mid_card-uv_mtch_rqst"
    | "list_end_card-uv_mtch_rqst"
    | "fold_chip-select"
    | "fold_card-uv_mtch_rqst"
    | "fold_card-uv_rgn_cmp"
    | "hub_cost-uv_rgn_cost"
    | "list_mid_card-opn_mdl"
    | "fixed_cta-scrl_to_form"
    | "cost_tab-total"
    | "cost_tab-parlor"
    | "prlr_pick-opn_sheet"
    | "prlr_pick-toggle"
    | "mubinso_btm-toggle";
  region_compare:
    | "chip-select"
    | "sido-answer"
    | "sigungu-answer"
    | "condolers-answer"
    | "status-answer";
  region_cost:
    | "status-answer"
    | "sido-answer"
    | "sigungu-answer"
    | "condolers-answer"
    | "method-select"
    | "move-prev"
    | "move-next";
  mubinso_quick:
    | "sido-answer"
    | "sigungu-answer"
    | "status-answer"
    | "condolers-answer";
  region_quick:
    | "sido-answer"
    | "sigungu-answer"
    | "status-answer"
    | "condolers-answer";
  directors_parlor:
    | "detail_CTA-uv_match_request"
    | "list_CTA-uv_match_request"
    | "directors_parlor_list-profle_and_review";
  parlor_estimates:
    | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    | "fixed_uv_mtch_rqst"
    | "fixed_call"
    | "top-uv_mtch_rqst"
    | "bottom-uv_mtch_rqst"
    | "content-uv_funeral_prepare_6"
    | "binso_content-see_details";
  parlor_detail: // 장례식장 전화 CTA 를 누르면 뜨는 문의 유형 모달.
  // ask-visit(조문)만 장례식장 직통, 나머지 셋은 고이 1666-9784 로 건다.
  | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    // 기존에 customEvent 가 없던 전화 CTA 들. 모달 오픈 분모로 쓰려고 새로 단다
    | "btn_call_info"
    | "mdl_disc-call"
    | "mdl_fnrl-call"
    | "tab_consult"
    | "compare-CTA"
    | "event_banner-match_request"
    | "event_banner-call"
    // 상세 페이지 신규 리드 카드 (NotSafe). 무빈소 빠른확인 카드 / 비용비교 인라인 카드
    | "mbn_card-sido"
    | "mbn_card-sgg"
    | "mbn_card-status"
    | "mbn_card-submit"
    | "quick_box-submit"
    | "cmp_card-view"
    | "cmp_card-open"
    | "cmp_card-status"
    | "cmp_card-submit"
    | "bottom_more-call"
    | "parlor_detail-go_PARLOR30V2_contract"
    | "image_banner-contract_parlor"
    | `estimate_card_${string}-uv_match_request`
    | "detail_compare_estimate"
    | "estimate_CTA-uv_match_request"
    | "close-chatbot"
    | "xv_mdl-uv"
    | "xv_mdl-close"
    | "xv_mdl-submit"
    | "chatbot-open"
    | "chatbot_answer-checklist"
    | "chatbot-call"
    | "chatbot_answer-parlor_compare"
    | "chatbot_answer-funeral_process"
    | "chatbot_answer-uv_match_request"
    | "chatbot_answer-what_to_do"
    | "funeral_director-uv_funeral_prepare_1"
    | "funeral_director-profile_and_review"
    | "funeral_diretor-uv_match_request"
    | "image_banner-uv_how_about_parlor_funeral"
    | "bagaji-call"
    | "shrtcut_btn-second"
    | "shrtcut_btn-file_request"
    | "shrtcut_btn-uv_how_about_parlor_funeral"
    | "shrtcut_btn-funeraldirector_uv_mtch_rqst"
    | "shrtcut_btn-yugol_call"
    | "shrtcut_btn-jedan_call"
    | "shrtcut_btn-uv_funeral_expense"
    | "shrtcut_btn-uv_review"
    | "package_selection-select_package"
    | "package_selection"
    | "package-uv_service"
    | "package-call"
    | "package_reserve-uv_match_request"
    | "detali-uv_service"
    | "chip-sangjo_selector"
    | "tab-expected-detail-price"
    | "sido-compare-test"
    | "sigungu-compare-test"
    | "visit_test1_flower"
    | "visit_test2_info"
    | "deceased_test1_call"
    | "deceased_test2_process_chat"
    | "deceased_test3_price"
    | "deceased_test0_match_request"
    | "deceased_test4_match_request"
    | "uv_tip_parlor_price"
    | "call_discount"
    | "CTA-uv_parlor_information_price"
    | "footer_floating-checklist"
    | "inline_checklist_recommendation_memo-option"
    | "inline_checklist_request_mdl-open"
    | "inline_mubinso_cost-uv_mth_req"
    | "inline_checklist_request_mdl-close"
    | "inline_checklist_mdl-try_submit"
    | "inline_checklist_mdl-submit_success"
    | "file_request_mdl-number"
    | "file_request_mdl-email"
    | "file_request_mdl-close"
    | "file_request_mdl-try_submit"
    | "file_request_mdl-submit_success"
    | "step_guide-CTA_uv_match_request"
    | "step_guide-fixed_price_detail"
    | "step_guide_CTA_right-uv_match_request"
    | "step_guide_CTA_left-call"
    | "step_guide-service_detail"
    | "price_goi_chip"
    | "price_parlor_chip"
    | "CTA_custom_cost"
    | "CTA_accurate_cost"
    | "uv_funeral_cost"
    | "uv_compnay_introduction"
    | "CTA-uv_match_request_real_link"
    | "selected_review-uv_review"
    | "price-tab_review"
    | "review-button_category"
    | "review-uv_match_request"
    | "review-call"
    | "review_detail"
    | "special_a1-answer"
    | "special_a1-call"
    | "special_a2-call_close"
    | "special_a2-call"
    | "special_a2-match_request"
    | "special_a1-match_request"
    | "special_a3-answer"
    | "special_a4-call"
    | "special_a4-match_request"
    | "special_a4_2-match_request"
    | "reasonable_funeral"
    | "call-sangjo_service"
    | "call-binso_discount"
    | "call-sangbok_daeyeo"
    | "modal_A-uv_how_about_parlor_funeral"
    | "step_guide_fixed_button"
    | "modal-close"
    | "compare_modal-discount_other"
    // 식장 복수 방문 비교 리드 모달 (액션 002 / Hackle #329)
    | "uv_compare_mdl"
    | "compare_mdl-close"
    | "compare_mdl-consent"
    | "compare_mdl-cta"
    | "compare_mdl-submit"
    | "uv_visited_list_chip"
    | "visited_list_chip-click"
    | "uv_visited_list_mdl"
    | "visited_list-x"
    | "visited_list-bg"
    | "visited_list-done"
    | "visited_list-intro"
    | "visited_list-s1"
    | "visited_list-s2"
    | "visited_list-guest"
    | "visited_list-submit"
    | "match_request-continue"
    | "discount_detail-open_discount_modal"
    | "modal_discount-CTA"
    | "modal_discount-close"
    | "quick_consult-quickcall_modal"
    | "footer_lft-quickcall_modal"
    | "compare_modal_ftr_lft-uv_about_parlor_funeral"
    | "compare_modal_ftr_rght-uv_parlor_region"
    | "goi_srvc_recomm-uv_match_request"
    | "see_detail-uv_about_parlor"
    | "common_review_section"
    | "CTA"
    | "CTA-onestop"
    | "CTA-parlor"
    | "CTA-facility"
    | "CTA-woodland"
    | "modal_detail_CTA-google_form_package"
    | "modal_seemore-google_form_package"
    | "modal_seemore"
    | "modal_detail_CTA"
    | "modal_detail_CTA-uv_guide_about_parlor_funeral"
    | "modal_seemore-uv_guide_about_parlor_funeral"
    | "modal_recommendation-uv_guide_about_parlor_funeral"
    | "modal_recommendation_CTA"
    | "parlor_review_mdl-close"
    | "parlor_review_mdl_ftr_rght-uv_match_request"
    | "parlor_review_mdl_ftr_left-uv_funeral_detail"
    | "review_CTA-mdl_review_parlor"
    | "sigungu_parlors_price_dtl-uv_sigungu_parlors"
    | "brdcrmb-uv_mtch_recomms"
    | "brdcrmb-uv_parlor_region_service"
    | "brdcrmb-uv_sido_parlors"
    | "brdcrmb-uv_sigungu_parlors"
    | "modal_close-close_top"
    | "modal_close-close_bottom"
    | "modal_detail_CTA-uv_funeral_prepare_1"
    | "uv_funeral_prepare_1"
    | "uv_funeral_prepare_2"
    | "uv_funeral_prepare_5"
    | "modal_seemore-uv_guidebook"
    | "modal_recommendation-uv_match_request"
    | "post_or_check_review-post_or_manual_review"
    | "copy-copy_address"
    | "info-tab_info"
    | "price-tab_price"
    | "packageType0_chip-expected_price"
    | "packageType1_chip-expected_price"
    | "packageType2_chip-expected_price"
    | "packageType_details-0"
    | "packageType_details-1"
    | "packageType_details-2"
    | "check_discount-uv_match_request"
    | "check_total_funeral_cost_CTA-uv_match_request"
    | "why_expensive-uv_guidebook_tip_before_funeral_expense"
    | "footer_bugo-bugo"
    | "footer_compare-open_compare_modal"
    | "compare_modal-close_top"
    | "compare_modal_others-uv_parlor_detail"
    | "brdcrmb-uv_sido_parlors"
    | "brdcrmb-uv_sigungu_parlors"
    | "prepare_package-uv_p_p"
    | "send_flower_email"
    | "send_sangbok_email"
    | "send_benefit_request_email"
    | "benefit_request_selected"
    | "benefit_request_submit"
    | `content-uv_${string}`
    | "uv_service_btm"
    | "file_request_mdl-marketing_agree"
    | "file_request_mdl-marketing_decline"
    | "inline_checklist_mdl-marketing_agree"
    | "revisit_mdl-submit"
    | "care_lead_mdl-submit"
    // 장례식장 상세 가격 공개 실험(짝수 id) 아코디언 펼침. 펼칠 때만 발화
    | "acc_price_binso"
    | "acc_price_fee"
    | "acc_price_faq"
    | "acc_price_esky"
    // 장례식장 상세 예상 비용 카드의 무빈소 설명, 가능 여부 확인 링크 (/match/request?template=mubinso). properties: parlor_id
    | "cost-mubinso_req"
    // 장례식장 상세 가이드북 카드 (실험군, 대조군 공통). properties: parlor_id, href, position
    | "guide_card"
    // 장례식장 상세 상황별 바로가기 (실험군, 대조군 공통). properties: parlor_id
    | "intent-call"
    | "intent-cost"
    | "intent-visit";
  parlor_detail_request:
    | "pyeong-answer"
    | "rec_shown"
    | "rec_avail"
    | "rec_unavail"
    | "rec_accept"
    | "rec_change"
    | "";
  parlor_detail_price:
    | "tab-price"
    | "discount_btn-uv_mtch_rqst_prlr"
    | "brdcrmb-uv_parlor_detail"
    | "call-hwahwan_bonaegi";
  // 장지 페이지 체크리스트 기존 코드를 위한 이벤트
  "facility-detail":
    | "brdcrmb-uv_mtch_recomms"
    | "details-uv_match_request"
    | "details-uv_mtch_rqst_fclty"
    | "banner_CTA-uv_p_p"
    | "modal_detail_close"
    | "modal_detail_CTA"
    | "modal_header_close"
    | "chk_total_cost_CTA-uv_home"
    | "chk_total_cost_CTA-uv_mtch_rqst"
    | "only_jangji_modal-tab_onestop"
    | "only_jangji_modal-continue"
    | "submit_conversion"
    | "jangji_only-answer"
    | "jangji_status-answer"
    | "jangji_type-answer"
    | "jangji_important_factor-answer"
    | "jangji_desired_service-answer"
    | "facility_funeral_sido-answer"
    | "facility_funeral_sigungu-answer"
    | "facility_sido-answer"
    | "facility_sigungu-answer"
    | "match_request-continue"
    | "compare_CTA-uv_mtch_rqst"
    | "step_guide_fixed_button"
    | "top_banner_CTA-uv_step-guide"
    | "facility-select_important_factor"
    | "facility_info-uv_facilities-search"
    | "visit_consult-call"
    | "chk_total_cost_CTA-uv_mtch_facil_req"
    | "information_price_top_CTA-uv_mtch_facility_rqst"
    | "information_price_bottom_CTA-uv_mtch_facility_rqst"
    | "call_discount"
    | "top_CTA-uv_mtch_facility_rqst"
    | "CTA-uv_facil_relocate";
  facility_detail: // "우리 지역 장례 비용" 카드(LocalCostCard) 클릭 → www 장례 비용 계산기 (실험 #361~#363)
  | "costcard-calc"
    | "care_lead_mdl-submit"
    | "xv_mdl-uv"
    | "xv_mdl-close"
    | "xv_mdl-submit"
    | "brdcrmb-uv_mtch_recomms"
    | "details-uv_match_request"
    | "details-uv_mtch_rqst_fclty"
    | "banner_CTA-uv_p_p"
    | "modal_detail_close"
    | "modal_detail_CTA"
    | "modal_header_close"
    | "chk_total_cost_CTA-uv_home"
    | "chk_total_cost_CTA-uv_mtch_rqst"
    | "only_jangji_modal-tab_onestop"
    | "only_jangji_modal-continue"
    | "submit_conversion"
    | "jangji_only-answer"
    | "jangji_status-answer"
    | "jangji_type-answer"
    | "jangji_important_factor-answer"
    | "jangji_desired_service-answer"
    | "facility_funeral_sido-answer"
    | "facility_funeral_sigungu-answer"
    | "facility_sido-answer"
    | "facility_sigungu-answer"
    | "match_request-continue"
    | "compare_CTA-uv_mtch_rqst"
    | "step_guide_fixed_button"
    | "top_banner_CTA-uv_step-guide"
    | "facility-select_important_factor"
    | "facility_info-uv_facilities-search"
    | "visit_consult-call"
    | "chk_total_cost_CTA-uv_mtch_facil_req"
    | "information_price_top_CTA-uv_mtch_facility_rqst"
    | "information_price_bottom_CTA-uv_mtch_facility_rqst"
    | "call_discount"
    | "top_CTA-uv_mtch_facility_rqst"
    | "CTA-uv_facil_relocate"
    | "CTA-cost_from_my_condition"
    | "nearby_fclty"
    | "nearby_prlr"
    | "nearby_crmtrm"
    | "addr_copy"
    | "CTA-pre_consultation"
    | "jangji_banner_click"
    | "prep_checklist_cta"
    | "other_prlr_cta"
    | "other_crmtrm_cta"
    // 비용 허브 카드. 좌측 01 은 기존 chk_total_cost_CTA-uv_mtch_facil_req 를 그대로 쓴다.
    | "cost_hub_total"
    // 하단 고정 바도 본문 카드와 같은 2개 구조라 표면별로 나눈다
    | "cost_hub_fix_detail"
    | "cost_hub_fix_total";
  facility_guide: `step_${number}_next` | `step_${number}_prev` | "final_cta";
  facility_match_recommendation:
    | "check_previous_result-uv_consult-list"
    | "recommend-uv_match_request"
    | "quickcall";
  /**
   * 장지 견적 접수 완료 화면 O/X 퀴즈.
   * facility_match_recommendation(29자)은 GA4 40자 truncate 여유가 4자뿐이라 짧은 path를 새로 뒀다.
   */
  jangji_done:
    | `quiz_q${number}_answer_o`
    | `quiz_q${number}_answer_x`
    | `quiz_next_q${number}`
    /** 끝까지 본 뒤 발화. 숫자는 오답 수 */
    | `quiz_done_${number}`
    | "reset"
    /**
     * 스텝 가이드 (실험 #336 B안).
     *
     * path 가 11자라 trigger 는 앞 22자가 살아남는다. 아래 값은 전부 15자 이하이고 기존
     * quiz_*, reset 과 앞부분이 겹치지 않는다.
     *
     * 스텝 이동은 도착한 스텝 번호를 붙인다. 전화 진입은 이 path 를 쓰지 않는다.
     * 헤더와 마지막 스텝의 "지금 바로 문의하기" 는 기존 quickcall
     * (facility_match_recommendation)을 그대로 쓴다. 이 실험 판정에 goi_call 이 들어가서
     * 이름을 끊으면 안 된다.
     */
    | `guide_next_${number}`
    | `guide_prev_${number}`
    | "guide_cta_total"
    /**
     * 여유(`탐색`) 리드 상담 필요도 설문. 선택지 원문은 `text` 로 넘긴다.
     * 앞 22자 기준으로 위 quiz_*, reset, guide_* 와 겹치지 않는다.
     */
    | "srvy_opt"
    | "srvy_submit"
    /**
     * 야간, 주말 접수 고객의 상담 희망 시간. 슬롯은 `text` 로 넘긴다("09:00-10:00").
     *
     * 견적(`match_recommendation_detail`)과 같은 trigger 이름을 쓴다. 같은 기능이라
     * 이름을 맞추면 채널 간 비교가 된다. `facility_match_recommendation`(29자)이 아니라
     * 이 path 를 쓰는 이유는 GA4 40자다. 그쪽은 trigger 가 4자만 남아 db_slot-select 와
     * db_submit-cta 가 둘 다 `db_s` 로 잘린다. `jangji_done` 은 22자가 살아남는다.
     */
    | "db_slot-select"
    | "db_submit-cta"
    | "db_confirm-cta";
  facility_search: "search_result-uv_facility";
  funeral_facilities:
    | "tab-uv_list"
    | "calc-uv_list"
    | "bn_review"
    | "bn_match"
    | "card"
    | "filter"
    | "sort"
    | "reset"
    | "back"
    | "faq"
    | "filter_apply"
    | "filter_chip"
    | "promo"
    | "sido"
    | "sigungu"
    | "visitors"
    | "sort_item"
    | "memo"
    | "info"
    // 리스트 하단 "장례 시설 한번에 추천받기" 시트. rec_ 접두로 메인 계산기(calc-uv_list, sido 등)와 구분
    | "rec_cta"
    | "rec_close"
    | "rec_sido"
    | "rec_sigungu"
    | "rec_visitors"
    | "rec_submit"
    // 견적 제출 후 로그인 이동(useHandleSendMemoAndRedirectToLogin 이 path 로 발화)
    | "mdl_check_result"
    // 로그인 되짚기 화면. text 로 rec / cmp 구분
    | "recap_view"
    | "recap_kakao"
    | "recap_nokakao"
    // 장례식장 비교하기. cmp_toggle text = on|off:id:식장명, cmp_max·cmp_remove text = id
    | "cmp_toggle"
    | "cmp_max"
    | "cmp_pill"
    | "cmp_remove"
    | "cmp_add"
    | "cmp_close"
    | "cmp_submit"
    // 비교함이 있을 때 리스트 이탈 확인 모달. text = back | link
    | "leave_stay"
    | "leave_exit";
  review_detail: // "우리 지역 장례 비용" 카드(LocalCostCard) 클릭 → www 장례 비용 계산기 (실험 #361~#363)
  | "costcard-calc"
    | "review_detail-other_review_detail"
    | "funeral_detail_card-uv_funeral_detail"
    | "recommend_other_funerals_card-uv_home"
    | "back_to_review_list-uv_review_list"
    | "btm_fixed_CTA-checklist"
    | "btm_fixed_CTA-uv_mth_req"
    // 후기 상세 개편 B안 (ReviewDetailRenewalContainer). rnw = renewal
    | "rnw_product_bnr-landing"
    | "rnw_director-uv_profile"
    | "rnw_parlor_req-uv_match"
    | "rnw_parlor_more-uv_parlor"
    | "rnw_cost_sel-region"
    | "rnw_cost_sel-scale"
    | "rnw_cost_cta-mdl_open"
    | "rnw_cost_mdl-try_submit"
    | "rnw_cost_mdl-submit_success"
    | "rnw_cost_mdl-close"
    | "rnw_cost_mdl-urgency"
    | "rnw_similar_chip-filter"
    | "rnw_similar_card-uv_review"
    | "rnw_similar_more-uv_review_list"
    | "rnw_header-back";
  review: // 하단 "우리 지역 장례 비용" 카드(LocalCostCard) A/B. → www 장례 비용 계산기
  | "costcard-calc"
    | "pbanner-call"
    | "pbanner-uv_match_request"
    | "pbanner-b1_prime"
    | "pbanner-b2_prime"
    | "pbanner-b3_premium"
    | "banner_CTA-uv_match_request"
    | "selector_sangjo-open_drawer"
    | "selector_sangjo_service-open_drawer"
    | "drawer_sangjo-tab"
    | "drawer_sangjo-service-tab"
    | "drawer_sangjo-detail"
    | "drawer_sangjo-service-detail"
    | "drawer_close"
    | "drawer_complete-select"
    | "check_goi_service-uv_home"
    | "review-uv_review_detail"
    | "top_review_section-uv_review_detail"
    | "active_clicks"
    | "rqst_btn-uv_mtch_rqst"
    | "call_btn-call"
    | "review_page-other_page"
    | "pbanner_cta-call"
    | "pbanner_cta-promotion"
    | "uv_summary_after"
    | "uv_home"
    | "sido-select"
    | "btm_fixed_CTA-checklist"
    | "btm_fixed_CTA-uv_mth_req"
    // 후기 메인 개편 B안 (?renewal=B 미리보기)
    | "rnw_filter_region-open"
    | "rnw_filter_product-open"
    | "rnw_sheet_sido-select"
    | "rnw_sheet_sigungu-select"
    | "rnw_sheet_product-select"
    | "rnw_sheet_region-apply"
    | "rnw_sheet_product-apply"
    | "rnw_cond-reset"
    | "rnw_cond_chip-remove"
    | "rnw_kw_chip-select"
    | "rnw_kw_filter-select"
    | "rnw_hcard-uv_review_detail"
    | "rnw_vcard-uv_review_detail"
    | "rnw_more_card-filter"
    | "rnw_more_btn-filter"
    | "rnw_banner-uv_prime"
    | "rnw_banner-uv_mubinso"
    | "rnw_banner-uv_memorial"
    | "rnw_more_lnd-mubinso"
    | "rnw_more_lnd-prime"
    | "rnw_empty-reset"
    | "rnw_few-reset"
    | "rnw_write-uv_review_post";
  notice_detail:
    | "kakao_start-uv_login"
    | "go_application-open_kakao_chat"
    | "confirm-uv_login";
  parlor_only_match_request:
    | "parlor_price-answer"
    | "parlor_factor-answer"
    | "submit_conversion";
  // 장례식장 비용 안내 리드 퍼널(match/parlor-requests 실험 B안, 4스텝 + 제출/완료).
  // 전환 판정 이벤트(match_request_complete / funnel_done / submit_conversion)는 기존 path를
  // 그대로 쓴다. 여기 있는 건 새 퍼널 고유의 스텝/선택 이벤트뿐이다.
  // path 11자 -> trigger 예산 22자. 아래 전부 14자 이하라 truncate 충돌 없음.
  parlor_cost:
    | "uv"
    | "step5_view"
    | "call_link"
    | "cta_kakao"
    | "cta_sheet"
    | "sheet-submit"
    | "sheet-success"
    | "sheet-close"
    | "guests-0"
    | "guests-50"
    | "guests-100"
    | "guests-200"
    | "urgency-now"
    | "urgency-week"
    | "urgency-month"
    | "urgency-notyet"
    | `step_next-${number}`
    | `step_prev-${number}`;
  parlor_match_request: // 비용 니즈 이탈 붙잡기 실험 B안. path가 20자라 trigger는 앞 13자만 GA4에 남는다.
  | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    // 기존 open_b4_modal, b4_modal_quit 등과 앞 13자가 겹치지 않는지 확인 완료.
    | "cst_rtn_trig"
    | "cst_rtn_open"
    | "cst_rtn_back"
    | "cst_rtn_next"
    | "cst_rtn_sbmt"
    | "cst_rtn_done"
    | "cst_rtn_gd"
    | "cst_rtn_x"
    | "uv_parlor_match_request"
    | "parlor_price-answer"
    | "parlor_factor-answer"
    | "status-answer"
    | "sido-answer"
    | "sigungu-answer"
    | "condolers-answer"
    | "submit_conversion"
    | "mdl_check_result"
    | `move-auto-${number}`
    // B안 quote LP 인트로 (실험 #298). 상/하단 CTA를 이벤트명에 직접 구분(property 분석 회피).
    // path 20자라 트리거 예산 13자: quote_cta_top(13자 풀), quote_cta_bottom(GA4서 quote_cta_bot로
    // truncate되나 top과 unique). 총 CTA는 GA4 이벤트명 quote_cta* prefix로 묶어 집계.
    | "quote_view"
    | "quote_cta_top"
    | "quote_cta_bottom"
    // 실험 #004 (parlor-requests 뒤로가기 모달). path 20자라 트리거 예산 13자 - 3개 모두 13자 풀.
    | "open_b4_modal"
    | "b4_modal_quit"
    | "b4_modal_cont"
    | "exit_compare"
    | "exit_mubinso"
    | "exit_service"
    | "hdr_mubinso"
    | "hdr_compare"
    // 다이렉트 진입 뒤로가기 트랩(popstate)로 모달이 열린 경우 open_b4_modal과 함께 추가 발화.
    // open_b4_modal(공통)과 분리해 새로 잡는 뒤로가기 모수를 측정. 11자 (예산 13자 내).
    | "b4_modal_fv"
    // 첫문항 니즈 캡처 실험(신규). 니즈 세그 분포 측정용. path 20자라 트리거 예산 13자 - 4개 모두 13자 내.
    | "nd_cost"
    | "nd_compare"
    | "nd_first"
    | "nd_nobinso"
    // 무빈소·상조 니즈 안내 2스텝 노출 + 리드 모달 오픈. path 20자, 트리거 예산 13자 내.
    | "mbn_step1"
    | "mbn_step2"
    | "mbn_lead_open"
    // 니즈별 뒷퍼널 실험 B안: 비교/처음 안내 스텝 노출 + 리드 모달 오픈. 트리거 예산 13자 내(전부 13자 이하).
    | "cmp_step1"
    | "fst_step1"
    | "cmp_lead_open"
    | "fst_lead_open"
    // 비교 스텝 인근 장례식장 picker 토글. on/off 분리해 선택 개수 분포 측정.
    | "cmp_pick_on"
    | "cmp_pick_off"
    // "원하는 곳이 없어요"(이 장례식장만) 탈출 선택지. 비교 니즈인데 비교를 원치 않는 비율 측정.
    | "cmp_pick_none"
    // 견적 제출 성공 시점의 니즈. 홈질문요청 쪽은 이미 path별로 쪼개져 있어(parlor_{mbn,cmp,fst}의
    // lead_mdl-success) 대칭을 맞추려면 견적 쪽에도 세그 구분이 필요하다.
    // properties 대신 이벤트명으로 쪼개는 이유: Hackle 지표 설정에서 properties 지원 여부에 의존하지 않기 위해.
    // 특히 B안에서 뒷퍼널을 보고 이전 눌러 견적으로 돌아간 케이스를 가드레일로 잡는 데 쓴다.
    // path 20자 → 예산 13자. 전부 10자 이하.
    | "qt_nd_cost"
    | "qt_nd_mbn"
    | "qt_nd_cmp"
    | "qt_nd_fst"
    | "qt_nd_none"
    // 뒷퍼널 실험 A/B 공통 전환 이벤트. 이 퍼널에서 "요청 제출을 끝냈다"를 arm/니즈와 무관하게 하나로 잡는다.
    // A안 compare/first는 견적 제출로 끝나고 B안은 홈질문요청 리드로 끝나 전환 이벤트명이 달라지는데,
    // 그대로 두면 Hackle primary metric을 어느 쪽으로 잡아도 반대편 arm이 0으로 나온다.
    // 견적 제출 성공 / 홈질문요청 리드 성공 두 지점 모두에서 발화. 11자 (예산 13자 내).
    | "funnel_done"
    // now 콜 확대 첫 화면 임종 배너 전화 버튼 (backlog "now 콜 확대: 첫 화면 임종 배너").
    | "urgent_bnr_call"
    // 위 배너가 스크롤로 사라진 뒤 하단에 뜨는 전화 버튼. 앞 13자가 urgent_bnr_ca 와 다르다.
    | "urgent_flt_call";
  parlor_match_request_passed_away: "";
  facility_match_request:
    | "only_jangji_modal-tab_onestop"
    | "only_jangji_modal-continue"
    | "temp_goicall"
    | "submit_conversion"
    | "jangji_only-answer"
    | "jangji_status-answer"
    | "jangji_type-answer"
    | "jangji_important_factor-answer"
    | "jangji_desired_service-answer"
    | "facility_funeral_sido-answer"
    | "facility_funeral_sigungu-answer"
    | "facility_sido-answer"
    | "facility_sigungu-answer"
    | "mdl_check_result";
  match_request: // 비용 니즈 이탈 붙잡기 실험 B안 (wiki/features/cost-need-exit-retention.md).
  // parlor_match_request(20자)와 공용 trigger라 GA4 40자 제한상 13자를 넘기지 않는다.
  | "cst_rtn_trig"
    | "cst_rtn_open"
    | "cst_rtn_back"
    | "cst_rtn_next"
    | "cst_rtn_sbmt"
    | "cst_rtn_done"
    | "cst_rtn_gd"
    | "cst_rtn_x"
    | "answer-dummy"
    | "submit_detail_request-non_member"
    | "footer_left-submit"
    | "footer_right-submit"
    | "compare_modal-continue"
    | "compare_modal-close"
    | "sangjo_compare_button"
    | "tab_all"
    | "tab_parlor"
    | "tab_facility"
    | "open_before_leave_modal"
    | "status-answer"
    | "sido-answer"
    | "sigungu-answer"
    | "condolers-answer"
    | "price_procedure-answer"
    | "facility-answer"
    | "procedure-answer"
    | "procedure-submit"
    | "recommendation_memo-option"
    | "check_result-uv_login"
    | "check_previous_result-uv_consult-list"
    | "before_leave_modal_quit-go_back"
    | "before_leave_modal-continue"
    | "quick_cnslt-opn_urgnt_mdl"
    | "mdl_check_result"
    | "learn_more-opn_match_content_mdl"
    | "submit_conversion"
    | "close_main_modal"
    | "prepare_package-uv_p_p"
    | "mdl_select-premium"
    | "urgent-memo-option"
    | "urgent-memo-call"
    | "submit-urgent-memo"
    | "worry-answer"
    | `move-prev-${number}`
    | `move-next-${number}`
    | `move-auto-${number}`
    | "move-auto-initial"
    | "move-next-initial"
    | "move-prev-procedure"
    | "move-next-procedure"
    | "move-auto-procedure"
    | "marketing_agree"
    | "marketing_decline"
    | "onboarding-next"
    // 실험 #342 B안 온보딩("세 가지 다" 니즈). 위 13자 규칙을 따른 약어다(an = all needs, ob = onboarding)
    | `an_ob_next_${number}`
    | "an_ob_sbmt"
    // now 콜 확대 첫 화면 임종 배너 전화 버튼 (backlog "now 콜 확대: 첫 화면 임종 배너").
    | "urgent_bnr_call"
    // 위 배너가 스크롤로 사라진 뒤 하단에 뜨는 전화 버튼. 앞 13자가 urgent_bnr_ca 와 다르다.
    | "urgent_flt_call";
  match_req_leave:
    | "drawer_close"
    | "open_file_request_modal"
    | "file_request_mdl-close"
    | "file_request_mdl-try_submit"
    | "file_request_mdl-submit_success"
    | "file_request_mdl-marketing_agree"
    | "file_request_mdl-marketing_decline";
  // 실험 #004 (parlor-requests 뒤로가기 체크리스트). match_req_leave와 동일 패턴.
  // path 15자라 file_request_mdl-* trigger들이 GA4 40자에서 truncate되지만
  // close/try_submit/submit_success/marketing 4-way 구분 가능 (marketing_agree/decline만 합쳐짐).
  parlor_req_quit:
    | "drawer_close"
    | "open_file_request_modal"
    | "file_request_mdl-close"
    | "file_request_mdl-try_submit"
    | "file_request_mdl-submit_success"
    | "file_request_mdl-marketing_agree"
    | "file_request_mdl-marketing_decline";
  // parlor-requests 무빈소·상조(④) 리드 모달 전용 path. 진용님 mubinso_funnel(#314)과 분리해 #315 지표 오염 방지.
  // path 10자 → trigger 앞 23자가 unique면 GA4 40자 안전. lead_mdl-* 최대 20자라 안전. (MubinsoLeadModal eventPath prop)
  parlor_mbn:
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "lead_mdl-close";
  // 니즈별 뒷퍼널 "비교" 리드 모달 전용 path. MubinsoLeadModal 재사용, 무빈소/처음 지표와 분리.
  // path 10자 → 트리거 예산 23자. lead_mdl-* 최대 20자라 안전.
  parlor_cmp:
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "lead_mdl-close";
  // 니즈별 뒷퍼널 "처음/절차" 리드 모달 전용 path. 현재 보조 CTA는 미사용(메인 단일).
  parlor_fst:
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "lead_mdl-close";
  // 장지 상세 - 장례 준비 관심 토픽(홈 질문 요청) 리드 모달 전용 path. MubinsoLeadModal 재사용, 무빈소 지표와 분리.
  // path 13자 → trigger 앞 20자 unique면 GA4 40자 안전. lead_mdl-* 최대 20자라 안전.
  facility_prep:
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "lead_mdl-close";
  // www 장례 비용 계산기. path 9자 → trigger 앞 24자가 unique해야 GA4 40자 안전. lead_mdl-* 는 MubinsoLeadModal(휴대폰 출구)
  // A(패키지 추천받기, 장지 체크리스트 CTA)와 B(카드)를 같은 키로 비교한다. text = 지면, properties.arm = A|B
  cost_card_ab: "slot_cta";
  cost_calc:
    | "landing"
    // 모든 카드 지면 공통 클릭 (text = 지면: guidebook, review_list, review_detail, hospice, care, facility)
    | "entry_card"
    | "region-next"
    | "scale-next"
    | "status-next"
    | "quote-created"
    | "r1-next"
    | "r2-next"
    | "r3-kakao"
    | "r3-phone"
    | "urgent-call"
    | "kakao-status"
    | "kakao-submit"
    | "kakao-success"
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "lead_mdl-close";
  // 무빈소 신규 리드 퍼널(design_handoff_mubinso_funnels). path 14자 → trigger 앞 19자가 unique해야 GA4 40자 안전.
  mubinso_funnel:
    | "landing-cta"
    // E4 개편 첫 화면의 급한 고객 보조 버튼(Q2 대신). 14자, path 14자 → 예산 19자 안.
    // 이어지는 모달 리드는 기존 urg-lead_try / urg-lead_ok 로 센다(A 의 임종·임박 진입과 같은 축)
    | "landing-urgent"
    | "q1-answer"
    | "q1-next"
    | "q1-prev"
    | "q2-answer"
    | "q2-next"
    | "q2-prev"
    // 응답별 선택 이벤트(v2, 순서 스왑: 첫질문=주제 / 둘째질문=상황). 선택 비율 분석용.
    | "topic-procedure"
    | "topic-cost"
    | "topic-parlor"
    | "topic-all"
    | "situation-passed"
    | "situation-days"
    | "situation-month"
    | "situation-later"
    | `funnel-next-${number}`
    | `funnel-prev-${number}`
    | "conv-info_cta"
    | "conv-cnslt_cta"
    | "conv-call"
    // 무빈소 퍼널 개편(2026-08, 실험 330). 진입(니즈)별 개별 이벤트.
    // GA4 는 속성보다 이벤트명으로 보는 게 쉬워서 퍼널별 지표를 이름으로 만든다.
    // urg=임종·임박, prc=절차, all=세가지 다, cst=비용.
    // 행동마다 공통 이벤트(conv-info_cta / lead_mdl-success / mubinso_request_complete)와
    // 함께 발화한다. 합산 지표에는 공통 이벤트만 쓸 것(둘을 더하면 두 번 세어진다).
    // A(대조군)에서도 같은 이름으로 발화해야 퍼널별 비교가 성립한다.
    | `${"urg" | "prc" | "all" | "cst"}-conv_view`
    | `${"urg" | "prc" | "all" | "cst"}-cta_main`
    | `${"urg" | "prc" | "all" | "cst"}-lead_try`
    | `${"urg" | "prc" | "all" | "cst"}-lead_ok`
    // E4 실험(369)에서 장례식장 찾기(fnd)도 같은 4종을 A · B 둘 다 쏜다. 최대 13자(fnd-conv_view)
    | `fnd-${"conv_view" | "cta_main" | "lead_try" | "lead_ok"}`
    // 리드 제출 성공 때 고른 상황(임종 · 임박 · 한 달 · 여유). A 는 Q2 답, E4 는 리드 폼 선택.
    // 접미사는 공통 임박도 키(goi_lead_*)와 같다. 최대 14자(prc-sit_notyet), {p}-lead_* 와 앞 19자가 다르다
    | `${"prc" | "all" | "cst" | "fnd"}-sit_${
        | "now"
        | "week"
        | "month"
        | "notyet"}`
    // B 전용(A 에 대응 화면이 없어 개별 이벤트만 둔다)
    | "prc-guide_lnk"
    // 가이드 창 제출 성공. 결과 화면 신청과 같은 lead_ok 로만 남아 둘을 나눠 보려고 둔다(A · E4 공통).
    // 12자, prc-guide_lnk 와 앞 19자 안에서 다르다
    | "prc-guide_ok"
    // E4 비용 퍼널(C) 결과 화면의 비용 후기 넘기기(화살표 · 점). 10자, path 14자 → 예산 19자 안. cst-* 다른 이벤트와 앞 19자가 다르다
    | "cst-review"
    // E4 찾기 퍼널(D) 결과 화면의 후기 넘기기. 10자, cst-review 와 같은 예산 안에서 앞 19자가 다르다
    | "fnd-review"
    // E4 절차 퍼널(B) 결과 화면의 후기 넘기기. 「모두 알아보기」 도 같은 화면이라 진입 구분 없이 하나로 둔다. 10자, prc-* 다른 이벤트와 앞 19자가 다르다
    | "prc-review"
    // E4 B · C · D 결과 후기를 처음 볼 때 그 후기 번호(2~5, 첫 장은 넘기지 않아도 보여 안 쏜다).
    // 「후기를 n개 이상 본 사람」 과 아닌 사람의 신청률을 비교하려는 것. 최대 12자(fnd-review-5), 앞 19자 안에서 서로 다르다
    | `${"prc" | "cst" | "fnd"}-review-${number}`
    | "urg-help_call"
    | "urg-cost_call"
    | "urg-proc_call"
    | "region-answer"
    // 관심 장례식장 체크(찾기 퍼널 B안). path 14자 → 예산 19자, 전부 9~11자로 잘리지 않는다.
    | "pick-view"
    | "pick-answer"
    | "pick-next"
    | "pick-skip"
    | "pick-more"
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "lead_mdl-close"
    | "guide-call"
    | "guide-book_1"
    | "guide-book_2"
    // B안 랜딩(네이버 SA 특정 소재 유입). path 14자 → trigger 앞 19자가 unique면 GA4 40자 안전.
    // landingB-cta=13자, landingB-review=15자, landingB-video=14자. 안전.
    | "landingB-cta"
    | "landingB-review"
    | "landingB-video"
    // M5 가족장 퍼널(가족장 소재 n_ad). path 14자 → 예산 19자, 전부 12~14자로 잘리지 않는다.
    // A/B 는 광고 소재 ID 로 가른다. Q1 · Q2 는 A 와 같은 이벤트를 쓰고(page_location 의 n_ad 로 구분),
    // 첫 화면 분기 카드와 퍼널 진입별 개별 이벤트만 fam 으로 따로 둔다(위 urg/prc/all/cst 와 같은 방식)
    | "fam-br_binso"
    | "fam-br_mubinso"
    | "fam-conv_view"
    | "fam-cta_main"
    | "fam-lead_try"
    | "fam-lead_ok"
    | "fam-guide_lnk";
  // 화장장 견적 퍼널(template=crematorium). path 12자 → trigger 앞 21자가 unique하면 GA4 40자 안전.
  crema_funnel:
    | "q1-answer"
    | "q2u-answer"
    | "q2a-answer"
    | "q3-answer"
    | "term_death-call"
    | "term_reloc-call"
    | `funnel-next-${number}`
    | "conv-cta"
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-close"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline"
    | "guide-call";
  // 후불제 상조 랜딩(실험 B). path 5자 → trigger 앞 28자가 unique하면 GA4 40자 안전.
  hubul:
    | "sticky_cta"
    | "sticky_call"
    | "review_more"
    | "answer_cta"
    | "form_submit_top"
    | "form_submit_mid"
    | "scroll_25"
    | "scroll_50"
    | "scroll_75"
    | "scroll_100";
  // 기초생활수급자 장례 혜택 랜딩(구 실험 #327, 상시 채택). path 11자 → trigger 앞 22자 unique면 GA4 40자 안전.
  beneficiary:
    | "fixed_cta"
    | "form_cta"
    | "sheet_submit"
    | "sheet_success"
    | "sheet_close"
    | "region-answer"
    | "status-answer"
    | "scroll_25"
    | "scroll_50"
    | "scroll_75"
    | "scroll_100";
  /** 상조 랜딩 `/match/request?template=sangjo` */
  sangjo_lp:
    | "landing_view"
    | "hero_btn_request"
    | "hero_btn_call"
    | "diff_btn_form"
    | "diff_btn_request"
    | `review_link_${number}`
    | "form_region-answer"
    | "form_sigungu-answer"
    | "form_guest-answer"
    | "form_status-answer"
    | "form_urgent_call"
    | "form_submit"
    | "form_submit_success"
    | "form_consult"
    | "sheet_status-answer"
    | "sheet_privacy_link"
    | "sheet_night_link"
    | "sheet_submit"
    | "sheet_success"
    | "sheet_fail"
    | "sheet_close"
    | "fixed_cta"
    | "fixed_call"
    | "exit_mdl_view"
    | "exit_mdl_prime"
    | "exit_mdl_leave"
    | "scroll_25"
    | "scroll_50"
    | "scroll_75"
    | "scroll_100";
  match_compare_service:
    | "is_prepaid_answer"
    | "company_name-answer"
    | "product_name-answer"
    | "other_info-answer";
  match_request_complete: "allow_call-toggle_button";
  match:
    | "recommendation-uv_match_request"
    | "recommendation_fixed_button-uv_match_request";
  /**
   * `/match/recommendation/relaxed` — 여유 + 재제출 세그먼트 전용 결과 페이지.
   *
   * 이 세그먼트는 아웃바운드 상담이 없어 "전환"으로 볼 액션이 약하다. 1차 지표는
   * **블록별 클릭 분포**이고, 그래서 트리거를 성격별 prefix 로 갈라 둔다.
   * 섞으면 "클릭은 많은데 아무 데도 안 갔다" 가 되어 지표가 무의미해진다.
   *
   * - `btn_` / `crd_` : **이탈 클릭**. 페이지 밖으로 나가는 진짜 지표
   *   (`crd_` 는 캐러셀 마지막 카드 안 CTA. 같은 문구의 하단 버튼과 위치를 구분한다)
   * - `slide_` / `arw_` : **제자리 클릭**. 캐러셀을 보는지 판별하는 진단용
   * - `view_` : **섹션 노출**. 스크롤로 어디까지 읽었는지
   *
   * path 가 3자라 트리거는 앞 30자가 unique 하면 된다(전부 여유 있음).
   *
   * 발화 규칙
   * - `slide_*` 는 2번째 슬라이드부터. 1번째는 로드하면 보이는 거라 page_view 와 중복이다.
   *   따라서 `slide_guide_2` 카운트 = "한 번이라도 넘긴 사람"
   * - `slide_*` 와 `view_*` 는 방문당 1회. 스크롤 왕복으로 중복 계상되면
   *   "몇 명이 봤나" 가 아니라 "몇 번 스쳤나" 가 된다
   */
  rlx:
    | "btn_call"
    | "btn_save_contact"
    | "btn_prime"
    | "crd_guide_more"
    | "btn_guide_all"
    | "crd_rvw_more"
    | "btn_rvw_all"
    | "btn_about_promise"
    | "btn_about_100won"
    | `slide_guide_${number}`
    | `slide_rvw_${number}`
    | "arw_guide_prev"
    | "arw_guide_next"
    | "arw_rvw_prev"
    | "arw_rvw_next"
    | "view_prime"
    | "view_guide"
    | "view_rvw"
    | "view_about";
  mtch_recomm_dtl_suggestions:
    | "CTA"
    | "youtube-prime"
    | "accordion_1"
    | "accordion_2"
    | "accordion_3";
  mtch_recomm_dtl: /**
   * 지역 무빈소 목록(#344 B)에서 비용을 물어볼 장례식장을 골라 제출.
   * path 가 15자라 트리거는 앞 18자가 unique 해야 한다. "mbns_" 로 시작하는 기존 트리거 없음
   */
  | "mbns_prlr_inquire"
    /** 무빈소 후기 섹션에서 후기 전문 보기 클릭. review id 는 properties 로 보낸다 */
    | "mbns_review"
    | "contract-prime"
    | "contract_check-prime"
    | "contract_change-prime"
    | "jangji_discount_call"
    | "ftr_btn_text_cnslt-see_more"
    | "ftr_btn_text_cnslt-kakao_chat"
    | "facility_request-uv_only_facility_match_request"
    | "consult_jangji-uv_match_facility_recommendation"
    | "parlor_request-uv_only_parlor_match_request"
    | "quickcall"
    | "why_parlor_recommended"
    | "banner_CTA-uv_p_p"
    | "arrw_dwn-opn_calculator"
    | "ftr_btn_cmn"
    | "mdl_cnslt_2_rsrv-need_urgent"
    | "mdl_cnslt_2_rsrv-timeslot"
    | "cnslt_rsrv_mdl_close"
    | "mdl_only_rsrv_2_make_rsrv-apply"
    | "mdl_cnslt_2_apply-uv_cnslt_dtl"
    | "mdl_only_rsrv_1-uv_rv_list"
    | "mdl_only_rsrv_1-rsrv_cmplt"
    | "mdl_cnslt_1_select_rsrv-step_2"
    | "mdl_only_rsrv_2-call_goi"
    | "qck_call_mdl-uv_rvs"
    | "qck_call_mdl-call_goi"
    | "qck_call_mdl-close"
    | "qck_call_mdl_accordion-opn_dtl"
    | "mdl_only_rsrv_1_select_rsrv-step_2"
    | "mdl_only_rsrv_2_apply-uv_cnslt_dtl"
    | "mdl_cnslt_1_cnslt_ongoing-uv_cnslt_dtl"
    | "mdl_cnslt_rsrv_mdl_close"
    | "mdl_cnslt_1_no_call-uv_cnslt_dtl"
    | "mdl_cnslt_2_apply-uv_cnslt_dtl"
    | "mdl_cnslt_1_no_call-apply_btn"
    | "oth_svc_mdl_uv_recomm_more"
    | "oth_svc_mdl_close"
    | "oth_svc_mdl-uv_mtch_recomm_lst"
    | "ftr_btn_qck_cnslt-opn_mdl_qck_call"
    | "ftr_btn_cmpr-uv_mtch_recomms_list"
    | "ftr_btn_more-opn_mdl_oth_svc"
    | "ftr_btn_qck_cnslt-uv_cslt_dtl"
    | "ftr_btn_rsrv_cnslt-opn_rsrv_mdl"
    | "cnslt_only_rsrv_mdl_close"
    | "ftr_btn_only_rsrv_cnslt-opn_rsrv_mdl"
    | "calculator_close"
    | "calc_init-cls_calc"
    | "price_chck-open_mdl_pricecheck"
    | "mdl_price_chck-uv_review"
    | "mdl_price_chck-check_price"
    | "mdl_price_chck-close"
    | "mdl_calc_mdl_parlor-done"
    | "mdl_calc_mdl_parlor_ftr_dontknow-select_need_cnslt"
    | "mdl_calc_mdl_parlor_ftr_select-cls_mdl"
    | "mdl_calc_mdl_jangji-done"
    | "mdl_calc_mdl_jangji_ftr_dontknow-select_need_cnslt"
    | "mdl_calc_mdl_jangji_ftr_select-cls_mdl"
    | "mdl_calc_mdl_jangji-jangji_type"
    | "calc_select-opn_mdl_parlor"
    | "calc_select-opn_mdl_jangji"
    | "youtube-video_played"
    | "uv_promotion_service"
    | "uv_prime"
    | "CTA_benefit"
    | `CTA_benefit_${"prime" | "promotion_service"}`
    | `CTA-${"main" | "sub"}`;
  mtch_recomm_lst:
    | "CTA-uv_match_request"
    | "CTA-uv_review"
    | "recomm_more-uv_mtch_recomm_dtl"
    | "no_result-uv_match_request"
    | "no_result-uv_review"
    | "btn_dtl-uv_match_recomm_dtl";
  // path 13자 → 트리거 20자까지 truncate 없이 살아남는다
  quote_pending: "btn_cost_guide";
  match_recommendation_detail: // daybreak: path 27자 → 트리거 앞 6자 unique (GA4 40자 truncate). daybreak_* 금지
  // 비용 니즈 실험(#340, #341) B안 결과 화면. 앞 7자 "cn_cost" 로 기존과 겹치지 않는다
  | "cn_cost_guide"
    | "db_slot-select"
    | "db_submit-cta"
    | "db_confirm-cta"
    | "funeral_prepare_6"
    | "private_jangji-call"
    | "hwajang-call"
    | "tab_funeral_prepare_youtube"
    | "tab_review"
    | "tab_funeral_director"
    | "sangjo_compare-report_result_click"
    | "sangjo_compare-call"
    | "main_recommendation_banner-detail_modal"
    | "modal_detail_close-close"
    | "review_detail-uv_review_detail"
    | "content_funeral_director-uv_funeral_prepare_1"
    | "content_how_to_choose-uv_guidebook_tip_before_5"
    | "footer_other_service-open_modal_other_service"
    | "modal_other_service_button_1-uv_home"
    | "modal_other_service_button_2-uv_notice_1"
    | "modal_other_service_button_3-uv_guidebook_summary_before"
    | "modal_other_service_close-close_modal"
    | "footer_start_consult-open_modal_consult"
    | "modal_consult_no_call-uv_consult_detail"
    | "modal_consult_select_reservation-modal_step_reservation"
    | "modal_consult_select_reservation-need_urgent"
    | "modal_consult_select_reservation-select_timeslot"
    | "modal_consult_select_reservation_apply_button-uv_consult_detail"
    | "modal_consult_select_reservation_urgent_call-uv_consult_detail"
    | "modal_consult-close"
    | "footer_ongoing_consult-uv_consult_detail"
    | "btn_call_consulting"
    | "btn_view_package"
    | "btn_saving_tips"
    | "jangji_banner_click"
    | `quiz_q${number}_answer_o`
    | `quiz_q${number}_answer_x`
    | `quiz_q${number}_result_cta`
    | `quiz_next_q${number}`
    | "quiz_complete_view_package"
    | "quiz_btn_chat_consulting"
    | "acc_cost"
    | "acc_form"
    | "acc_sangjo"
    | "acc_proc";
  login: // 비용 니즈 이탈 붙잡기 실험 B안. 로그인 페이지 이탈 시도 지점.
  | "cst_rtn_trig"
    | "cst_rtn_open"
    | "cst_rtn_back"
    | "cst_rtn_next"
    | "cst_rtn_sbmt"
    | "cst_rtn_done"
    | "cst_rtn_gd"
    | "cst_rtn_x"
    | `login_${string}`
    | `signup_${string}`
    | "nokakao_modal-close"
    | "nokakao-open_nokakao_modal"
    | "kakao_start-kakao_login"
    | "nokakao_start-nokakao_login"
    | "drawer_dormant_close-close_drawer"
    | "drawer_dormant_revert-revert_dormancy"
    | "modal_confirm-close_modal"
    | "footer_ongoing_consult-uv_consult_detail"
    | "call"
    | "chat"
    | "passed_away_checklist_modal"
    | "passed_away_nokakao_modal"
    | "parlor_req_kakao_start-kakao_login"
    | "parlor_req_nokakao-open_nokakao_modal"
    // 실험 #005-login: FileRequestModal 추가 정보 라디오. 옵션값은 text 파라미터로 발화 (임박도 `recommendation_memo-option` 패턴과 동일).
    | "file_request_mdl-info_opt";
  home:
    | "shrtcut_btn_prime-uv_prime"
    | "CTA_banner-call"
    | "CTA_banner-uv_match_request"
    | "pyojun_service-uv_service"
    | "main_banner_recommendation-uv_match"
    | "main_banner_prepare_atoz-uv_funeral_prepare_1"
    | "main_banner_compare_sangjo-uv_funeral_prepare_2"
    | "main_banner_see_difference-uv_funeral_prepare_3"
    | "banner_recommendation_button-uv_match_request"
    | "list_recommendation-uv_funeral_detail"
    | "list_price_satisfaction-uv_funeral_detail"
    | "list_review-uv_funeral_detail"
    | "list_professionalism-uv_funeral_detail"
    | "banner_guidebook_button-uv_notice_1"
    | "review_section_review_detail-uv_review_detail"
    | "review_section_all_reviews-uv_review"
    | "why_goi_trust_button-uv_funeral_prepare_3"
    | "how_to_use_goi-uv_funeral_prepare_1"
    | "drawer_recommendation_close-close"
    | "drawer_recommendation_button-uv_match_request"
    | "match_CTA_bottom-uv_match_request"
    | "review_button-uv_review"
    | "search_service-uv_funeral"
    | "complimentary_quotation_button-uv_match_request"
    | "top-status-answer"
    | "top-sido-answer"
    | "top-sigungu-answer"
    | "top-condolers-answer"
    | "bt-status-answer"
    | "bt-sido-answer"
    | "bt-sigungu-answer"
    | "bt-condolers-answer"
    | "top-submit_conversion"
    | "bt-submit_conversion"
    | "top-alredy_qutatin-uv_mch_recomm"
    | "bt-alredy_qutatin-uv_mch_recomm"
    | "top-alredy_qutatin-uv_bnf_prepare"
    | "bt-alredy_qutatin-uv_bnf_prepare"
    | "prviw_mtch-opn_mdl"
    | "prces_btn-uv_gdb_fnrl_prces"
    | "director_consult_btn-opn_mdl"
    | "goi_director_good_btn-opn_mdl"
    | "all_review_btn-uv_review"
    | "pr-uv_pr"
    | "intrviw-play_youtube"
    | "frst_prpre-uv_fnrl_prpre_1"
    | "frst_prpre-uv_fnrl_prpre_2"
    | "frst_prpre-uv_fnrl_prpre_3"
    | "qik_cal_btn-opn_mdl"
    | "24hour_btn-opn_mdl"
    | "chatting"
    | "goi_srvice-uv_srvice"
    | "ftr_circle_btn-uv_guidebook"
    | "ftr_circle_btn-uv_qna"
    | "ftr_circle_btn-uv_mtch_rqst"
    | "ftr_circle_btn-uv_parlor"
    | "ftr_circle_btn-uv_facility"
    | "ftr_circle_btn-uv_bugo"
    | "shrtcut_btn_mtch_rqst-uv_mtch_rqst"
    | "shrtcut_btn_parlor-uv_mtch_rqst"
    | "shrtcut_btn_facility-uv_mtch_rqst"
    | "shrtcut_btn_compare-uv_mtch_rqst"
    | "service_tab-binso0"
    | "service_tab-binso50"
    | "service_tab-binso100"
    | "service_tab-binso200"
    | "24hour_standby-opn_call_mdl"
    | "24hour_standby-uv_mtch_rqst"
    | "step_guide_fixed_button"
    | "top-consult-uv_funeral_prepare_1"
    | "bt-consult-uv_funeral_prepare_1"
    | "shrtcut_btn_intro-uv_cmpny_intro"
    | "shrtcut_btn_service-uv_service"
    | "shrtcut_btn_price-move_home_service"
    | "shrtcut_btn_prcss-uv_gidbok_prcss"
    | "shrtcut_btn_prpre-uv_fnrl_prpre_1"
    | "question-opn_mdl"
    | "send_email_mdl-close"
    | "send_email_mdl-btn"
    | "send_question_email"
    | "layout_CTA-uv_mtch_rqst"
    | "goi_service-uv_service"
    | "goi_msg-uv_fnrl_pre"
    | "goi_msg-uv_service"
    | `banner_tab_${0 | 1}`
    | `banner_CTA-uv_${"promotion_service" | "prime"}`
    | "marketing_agree"
    | "btm_fixed_CTA-uv_prime_return";
  home_sa_1: "";
  home_sa_2:
    | "call_mdl-header_close"
    | "call_mdl-opn_schedule"
    | "call_mdl-call"
    | "scedul_mdl-close"
    | "scedul_mdl-send_btn"
    | "send_email"
    | "service_binso0"
    | "service_binso50"
    | "service_binso100"
    | "service_binso200"
    // 실험 #005-not-login: FileRequestModal 추가 정보 라디오. 옵션값은 text 파라미터로 발화 (임박도 `recommendation_memo-option` 패턴과 동일).
    | "file_request_mdl-info_opt";
  guidebook: // 하단 "우리 지역 장례 비용" 카드(LocalCostCard) A/B. → www 장례 비용 계산기
  | "costcard-calc"
    | "ex_CTA_book-uv_notice_1"
    | "CTA_bk-uv_notice_1"
    | "uv_service"
    | "uv_about_parlor_funeral"
    | "call-yugolham"
    | "funeral_expense_youtube-video_played"
    | "funeral_expense_youtube_btm-uv_match_request"
    | "search-uv_guidebook_search"
    | "banner_blog-uv_blog"
    | "info-uv_guidebook_summary"
    | "tab_all-tab_all"
    | "tab_qna-tab_qna"
    | "chip_guidebook-before"
    | "chip_guidebook-ongoing"
    | "chip_guidebook-after"
    | "chip_guidebook-etc"
    | "content_detail-uv_guidebook_tip_before"
    | "content_detail-uv_guidebook_tip_ongoing"
    | "content_detail-uv_guidebook_tip_after"
    | "content_detail-uv_guidebook_tip_etc"
    | "post_qna_button-uv_guidebook_edit"
    | "post_qna_modal-post_question"
    | "want_call-call"
    | "chip_qna-qna_type_text"
    | "qna_detail_card-open_qna_modal"
    | "qna_modal-share"
    | "qna_modal-scrap"
    | "CTA_uv_guidebook"
    | "bottom-CTA_link_to_prime"
    | "btm_fixed_CTA-checklist"
    | "btm_fixed_CTA-uv_mth_req"
    | "CTA-uv_match_request"
    | "footer_lft-quickcall_modal";
  guidebook_search:
    | "tab_all-tab_all"
    | "tab_qna-tab_qna"
    | "content_detail-uv_guidebook_tip_before"
    | "content_detail-uv_guidebook_tip_ongoing"
    | "content_detail-uv_guidebook_tip_after"
    | "content_detail-uv_guidebook_tip_etc"
    | "qna_detail_card-open_qna_modal"
    | "qna_modal-share"
    | "qna_modal-scrap";
  gdb_tip:
    | "banner_CTA-checklist"
    | "youtube_btm-uv_youtube"
    | "youtube-video_played"
    | "footerCTA-uv_guide"
    | "footerCTA_fune_help"
    | "fune_help-uv_mth_req"
    | "fune_help-open_question"
    | "btm_fixed_CTA-uv_mth_req"
    | "btm_fixed_CTA-checklist"
    | `breadcrumb-uv_${string}`
    | `${string}-CTA_${number}`
    | "modal_CTA-call"
    | "modal_CTA-uv_match_request";
  gdb_tip_bfr:
    | "ex_CTA_total-uv_match_request"
    | "ex_fix_btn-uv_qna"
    | "ex_fix_btn-uv_match_request"
    | "ex_mdl_review-uv_review"
    | "ex_mdl_btn-uv_qna"
    | "ex_mdl_btn-uv_match_request"
    | "ex-status-answer"
    | "ex-sido-answer"
    | "ex-sigungu-answer"
    | "ex-condolers-answer"
    | "ex-alredy_qutatin-uv_mch_recomm"
    | "ex-alredy_qutatin-uv_bnf_prepare"
    | "ex-CTA"
    | "ex-youtube-video_played"
    | "ex-youtube_btm-uv_match_request";
  gdb_tip_cvs:
    | "CTA_total-uv_match_request"
    | "CTA_consult-uv_match_request"
    | "fix_btn-opn_mdl_other_service"
    | "fix_btn-opn_mdl_consult"
    | "mdl_other_service-uv_match_request"
    | "mdl_other_service-uv_funeral"
    | "mdl_other_service-uv_guidebk_qna"
    | "mdl_other_service-close_modal"
    | "mdl_consult-close_modal"
    | "mdl_consult-uv_guidebk_qna"
    | "mdl_consult-call_goi";
  funeral:
    | "banner_CTA-uv_match_request"
    | "tab_service-tab_service"
    | "tab_funeral-tab_funeral"
    | "filter_button-open_filter"
    | "service_chip_premium-view_premium"
    | "service_chip_without_binso-view_without_binso"
    | "service_chip_parlor_discount-view_parlor_discount"
    | "service_chip_filter-open_filter"
    | "filter_result_non-open_filter"
    | "service_list-uv_funeral_detail"
    | "funeral_chip-view_recommendation"
    | "funeral_chip-view_price_satisfaction"
    | "funeral_chip-view_review"
    | "funeral_chip-view_professionalism"
    | "list_recommendation-uv_funeral_detail"
    | "list_price_satisfaction-uv_funeral_detail"
    | "list_review-uv_funeral_detail"
    | "list_professionalism-uv_funeral_detail"
    | "more_recommend-view_funeral_tab"
    | "more_recommend-view_service_tab"
    | "more_recommend-uv_match_request"
    | "filter-condolers_0"
    | "filter-condolers_50"
    | "filter-condolers_100"
    | "filter-condolers_200"
    | "filter-price"
    | "filter-policies_1"
    | "filter-policies_2"
    | "filter-policies_3"
    | "filter-category_premium"
    | "filter-category_without_binso"
    | "filter-category_burial"
    | "filter-category_eco"
    | "filter-category_parlor_discount"
    | "filter-reset"
    | "filter-view_result"
    | "active-clicks";
  funeral_detail:
    | "tab_review-tab_review"
    | "tab_service-tab_service"
    | "tab_intro-tab_intro"
    | "tab_faq-tab_faq"
    | "post_review-uv_funeral_detail_review_post"
    | "select_order-select_order"
    | "review_detail-uv_review_detail"
    | "service_chip-view_service"
    | "service_card_see_details-service_detail"
    | "service_compare_price_see_details-service_detail";
  funeral_prepare_detail:
    | "step1-call"
    | "step2-call"
    | "step3-review"
    | "recommendation-uv_match_request"
    | "consult_button-uv_home";
  consult_list:
    | "tab_consultlist-tab_consultlist"
    | "tab_match-tab_match"
    | "sangjo_consult_detail_card-uv_consult_detail"
    | "sangjo_consult_detail_callchat_consult-uv_consult_detail"
    | "sangjo_consult_detail_service-uv_funeral_detail"
    | "sangjo_match_detail-uv_consult_detail";
  my_detail: "";
  service:
    | "detail-uv_service"
    | "top_prime-prime_9900"
    | `${string}_fixed-prime_start_contract`
    | `${string}-prime_start_contract`
    | "call_fixed_CTA"
    | "go_detail_top-premium"
    | "go_detail_btm-premium"
    | "premium_apply-open_consult"
    | "copy"
    | "uv_match_recommendation_detail"
    | "tab_type-view_type"
    | "tab_step-view"
    | "dropdown_deduction-view"
    | "dropdown_addons-view"
    | "pr-uv_pr"
    | "review-uv_review"
    | "premium_apply_fixed-open_consult"
    | "uv_company_introduction"
    | "scrmdl_mbn-view"
    | "scrmdl_mbn-cta"
    | "scrmdl_mbn-close"
    | "scrmdl_fam-view"
    | "scrmdl_fam-cta"
    | "scrmdl_fam-close"
    | "scrmdl_nrm-view"
    | "scrmdl_nrm-cta"
    | "scrmdl_nrm-close"
    | `chip_${string}`;
  product:
    | "modal-memorialtable_CTA-detail"
    | "modal-memorialtable_CTA-no_more_today"
    | "modal-memorialtable_CTA-close"
    // 실험 #005-not-login: FileRequestModal 추가 정보 라디오. 옵션값은 text 파라미터로 발화 (임박도 `recommendation_memo-option` 패턴과 동일).
    | "file_request_mdl-info_opt";
  guide_content:
    | "CTA_TOP-open-goi-consult-modal"
    | "open-goi-consult-modal"
    | "detail_1-uv_service"
    | "footer_lft-uv_home"
    | "footer_rght-open-goi-consult-modal";
  guide_about_parlor_funeral:
    | "bagaji-call"
    | "direct-call"
    | "estimate-uv_match_request"
    | "CTA_TOP-uv_match_request"
    | "summary_1-toggle"
    | "summary_2-toggle"
    | "summary_3-toggle"
    | "summary_4-toggle"
    | "detail_1-uv_service"
    | "detail_2-uv_match_request"
    | "detail_3-uv_funeral_detail_foreverlife"
    | "detail_3-open-goi-consult-modal"
    | "detail_4-uv_match_request"
    | "footer_lft-open-goi-consult-modal"
    | "footer_rght-uv_match_request";
  promotion:
    | "sign_up-uv_login"
    | "discount_register-open_mdl"
    | "select_service"
    | "send_email"
    | "CTA-promotion_button"
    | "CTA"
    | "CTA-open_phone_modal"
    | "phone_modal-submit";
  hospice_detail: // "우리 지역 장례 비용" 카드(LocalCostCard) 클릭 → www 장례 비용 계산기 (실험 #361~#363)
  | "costcard-calc"
    | "back-uv_hsps_serch"
    | "tab-info_tab"
    | "tab-facility_tab"
    | "tab-review_tab"
    | "CTA_call"
    | "CTA_uv_prime"
    | "memorialtable_banner_click"
    | "jangji_banner_click"
    | "banner_blog-uv_blog";
  hospice_search:
    | "dropdown-selct_sido"
    | "chips-selct_srvic_type"
    | "detail_card-uv_hospice_detail";
  funeral_prepare:
    | "frst_bt_btn-uv_review"
    | "frst_bt_btn-uv_mtch_rqst"
    | "frst_bt_btn-uv_prime"
    | "frst_bt_btn-call"
    | "six_bt_btn-uv_review"
    | "six_bt_btn-uv_mtch_rqst"
    | "six_bt_btn-uv_prime"
    | "six_bt_btn-call"
    | "scnd_here_click-uv_fnerl_prpre_1"
    | "scnd_bt_btn-uv_fnerl_prpre_1"
    | "parlor_price-fixed_uv_mtch_rqst"
    | "parlor_price-fixed_call"
    | "btm_fixed_CTA-checklist"
    | "btm_fixed_CTA-uv_mth_req";
  crematorium_detail:
    | "care_lead_mdl-submit"
    | "xv_mdl-uv"
    | "xv_mdl-close"
    | "xv_mdl-submit"
    | "modal-open"
    | "modal-close"
    | "modal-call"
    | "breadcrumb-uv_mtch_recmndtin"
    | "total_CTA-uv_home"
    | "copy-copy_address"
    | "reserve_CTA-call"
    | "call"
    // 전화 CTA(위 call, GNB 24시간 고객센터)를 누르면 뜨는 문의 유형 모달.
    // ask-booking / ask-etc 는 고이 1666-9784, ask-relocation / ask-visit 는 화장장 번호로 건다.
    // path(18자) + prefix 를 빼면 트리거는 15자까지라 relocation 외에는 줄이지 않았다.
    | "ask-booking"
    | "ask-relocation"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    | "support_call"
    | "tel_facility"
    | "nav_price"
    | "nav_process"
    | "nav_parking"
    | "nav_location"
    | "nav_faq"
    | `faq_${number}`
    | "bridge_reco"
    | "bridge_facility"
    | "xv_mdl-uv"
    | "xv_mdl-close"
    | "xv_mdl-submit"
    | "cta_cost"
    | "cta_booking"
    | "bottom_cta_cost"
    | "bottom_cta_booking";
  crematorium_region:
    | "modal-open"
    | "modal-close"
    | "modal-call"
    | "sido-dropdown"
    | "recommend-tab"
    | "list-tab"
    | "detail-uv_crematorium_detail";
  crematorium_recommendation:
    | "CTA"
    | "CTA-prepare-checklist"
    | "footer-CTA-prepare-checklist"
    | "CTA_banner-call"
    // 하단 sticky CTA 에서 뜨는 문의 유형 모달.
    // path 가 26자라 트리거는 7자에서 잘린다(ask-boo / ask-rel / ask-vis / ask-etc / ask-clo).
    // 잘린 뒤에도 서로 unique 하고 기존 트리거와도 안 겹쳐서 분석에 지장이 없다.
    | "ask-booking"
    | "ask-relocation"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    | "CTA-woodland"
    | "CTA-facility"
    | "reserve_CTA-call"
    | "consult"
    | "to_detail"
    | `planb_${number}`
    | "rsv_open"
    | "rsv2_open"
    | "rsv_submit"
    | "rsv_success"
    | "tel_facility";
  match_facility_request: "carousel_item" | `carousel_item_${number}`;
  match_facility_request_crematorium: "";
  consultant_search: "search_btn-search";
  facility_region:
    | "select_sido-uv_region_sido"
    | "anthr_facil-uv_facil_region"
    | "card-uv_facility"
    | "no_facility-uv_anthr_type"
    | "no_facility-uv_sido_region";
  sea_burial: "";
  sea_burial_region: "";
  sea_burial_detail: "";
  // 온드미디어발 리드 수집 페이지. path 13자 → trigger 앞 20자 unique면 GA4 40자 안전. 최대 trigger 18자(lead_mdl-mkt_decline)로 안전.
  owned_request:
    | "btn_cta"
    // 장례지도사 문의 폼(/owned-request/funeral-director). 문항 5개라 sel_reason_0~4
    | `sel_reason_${number}`
    | "btn_next"
    // 검증 통과 후 발화. 검증 전에 쏘는 lead_mdl-submit 과 구분하려고 try/ok 쌍을 쓴다
    | "lead_try"
    | "lead_ok"
    | "lead_mdl-submit"
    | "lead_mdl-success"
    | "lead_mdl-close"
    | "lead_mdl-mkt_agree"
    | "lead_mdl-mkt_decline";
  promotion_parlor_50: "call" | "uv_promotion_service_family";
  info_price_facil:
    | "CTA"
    | "CTA-uv_mtch_facil_req"
    | "bottom-CTA-uv_mtch_facility_rqst"
    | "step_guide_fixed_button"
    | "total_cost-uv_mth_req";
  facility_relocate: "submit_success";
  premium_2024: "sign_up_CTA";
  parlor_recomm_list: "parlor_recomm_list";
  post_paid_funeral_prepare: "content-uv_prepare_1" | "content-uv_prepare_2";
  campaign:
    | "benefit_20_hero_CTA"
    | "benefit_20_fixed_CTA"
    | "benefit_20_send_email"
    | "banner-memorialtable_review"
    | "memorialtable_review_fixed_CTA"
    | "modal-memorialtable_CTA-call"
    | "modal-memorialtable_CTA-chat";
  match_recommendation_tips:
    | "btn_prev"
    | "btn_next"
    | `btn_prev_${number}`
    | `btn_next_${number}`
    | "btn_product_detail"
    | "bs_product_detail_submit"
    | "bs_product_detail_close";
  promotion_bnf:
    | "uv_company_introduction"
    | "cta_register_now"
    | "fixed_button_register"
    | "tab_change";
  jangji_promotion:
    | "cta_hero"
    | "cta_fixed"
    | "cta_section1"
    | "cta_section2"
    | "cta_submit"
    | "submit_success";
  jangji_prime:
    | "cta_hero"
    | "cta_feature"
    | "cta_float_consult"
    | "cta_float_call"
    | "cta_submit"
    | "submit_success"
    | `sel_interest_${number}`
    | `sel_timing_${number}`
    | `sel_calltime_${number}`;
  jangji_retention:
    | "cta_hero"
    | "cta_feature"
    | "cta_float_consult"
    | "cta_float_call"
    | "cta_submit"
    | "submit_success"
    | `sel_interest_${number}`
    | `sel_calltime_${number}`;
  jangji_prepare:
    | "cta_hero"
    | "cta_sticky"
    | "cta_submit"
    | "submit_success"
    | `sel_method_${number}`
    | `sel_calltime_${number}`
    | `sel_timing_${number}`
    | "scroll_hero"
    | "scroll_problem"
    | "scroll_table"
    | "scroll_steps"
    | "scroll_trust"
    | "scroll_faq"
    | "scroll_form";
  jangji_blog:
    | "cta_hero"
    | "cta_feature"
    | "cta_float_consult"
    | "cta_float_call"
    | "cta_submit"
    | "submit_success"
    | `sel_interest_${number}`
    | `sel_timing_${number}`
    | "sel_region";
  consult_proposal:
    | "btn_contract_hero"
    | "btn_contract_package"
    | "btn_contract_fixed"
    | "btn_all_reviews"
    | "btn_detail_view"
    | "btn_share"
    | "scroll_hero"
    | "scroll_principles"
    | "scroll_ceo"
    | "scroll_recruit"
    | "scroll_partners"
    | "scroll_reviews"
    | "scroll_package"
    | "scroll_faq";
  consult_proposal2:
    | "btn_contract_hero"
    | "btn_contract_package"
    | "btn_contract_fixed"
    | "btn_share_hero"
    | "btn_share_fixed"
    | "scroll_hero"
    | "scroll_checkpoints"
    | "scroll_goi_answer"
    | "scroll_package";
  msg_hidden_cost:
    | "btn_phone_bottom"
    | "btn_checklist_cta"
    | "btn_checklist_bottom"
    | "btn_quote"
    | "nav_sec_01"
    | "nav_sec_02"
    | "nav_sec_03"
    | "nav_sec_04"
    | "nav_sec_05"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_sec_04"
    | "scroll_sec_05"
    | "scroll_checklist_cta"
    | "scroll_about";
  message_last_check:
    | "nav_sec_01"
    | "nav_sec_02"
    | "nav_sec_03"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_action"
    | "scroll_phone_cta"
    | "scroll_finale"
    | "scroll_perk"
    | "scroll_about"
    | "btn_quote_hero"
    | "btn_halls_sec_01"
    | "btn_phone_number"
    | "btn_phone_save"
    | "btn_phone_call"
    | "btn_perk_enroll"
    | "btn_quote_challenge"
    | "btn_quote_about"
    | "quote_modal_close"
    | "quote_submit"
    | "quote_success"
    | "btn_phone_bottom"
    | "btn_quote_bottom";
  cost_standard:
    | "nav_sec_01"
    | "nav_sec_02"
    | "nav_sec_03"
    | "scroll_hero"
    | "scroll_cost"
    | "scroll_sangjo"
    | "scroll_perk"
    | "scroll_lead"
    | "btn_quote_entry"
    | "btn_quote_closing"
    | "btn_quote_about"
    | "btn_perk_enroll"
    | "quote_modal_close"
    | "quote_submit"
    | "quote_success"
    | "btn_phone_bottom"
    | "btn_quote_bottom";
  msg_farewell:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_cost_grow:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_after7:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_family_decide:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_cost_items:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_good_funeral:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback"
    | "mt_btn_join"
    | "mt_btn_detail";
  msg_prep:
    | "btn_helpful"
    | "btn_copy_link"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_prep_advance:
    | "btn_helpful"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_mubinso:
    | "btn_helpful"
    | "btn_call"
    | "scroll_hero"
    | "scroll_sec_01"
    | "scroll_sec_02"
    | "scroll_sec_03"
    | "scroll_closing"
    | "scroll_feedback";
  msg_procedure:
    | "btn_call_top"
    | "btn_save_hero"
    | "btn_call_phone_number"
    | "btn_save_phone"
    | "btn_call_phone"
    | "btn_call_bottom"
    | "btn_save_bottom"
    | "btn_enroll"
    | "scroll_hero"
    | "scroll_process"
    | "scroll_customize"
    | "scroll_phone_cta"
    | "scroll_contract"
    | "scroll_about";
  urgent_bnr: // 임종 배너에서 연 문의 유형 모달. ask-visit(조문)만 장례식장 직통
  "ask-binso" | "ask-cost" | "ask-visit" | "ask-etc" | "ask-close";
  parlor_ad_quote: // 장례식장 전화 CTA 에서 뜨는 문의 유형 모달.
  // ask-visit(조문)만 장례식장 직통, 나머지 셋은 고이 1666-9784
  | "ask-binso"
    | "ask-cost"
    | "ask-visit"
    | "ask-etc"
    | "ask-close"
    | "btn_cta_quote"
    | "phone_call"
    | "btn_cta_bottom"
    | "btn_phone_header"
    | "btn_phone_hero"
    | "quick_mourner"
    | "quick_status"
    | "quick_success"
    | "quick_conflict"
    | "quick_error"
    // now 콜 확대 첫 화면 임종 배너 전화 버튼 (backlog "now 콜 확대: 첫 화면 임종 배너").
    | "urgent_bnr_call"
    // 위 배너가 스크롤로 사라진 뒤 하단에 뜨는 전화 버튼. 앞 13자가 urgent_bnr_ca 와 다르다.
    | "urgent_flt_call";
  funeral_cost_ad:
    | "btn_cta_quote"
    | "btn_cta_bottom"
    | "btn_phone_header"
    | "btn_phone_hero";
  nowake_landing: "section5_cta" | "bottom_cta" | "lead_modal" | "lead";
}

export enum HackleEventName {
  "회원가입" = "custom_sign_up",
  "로그인/회원가입 클릭" = "click_login_register",
  "로그인/회원가입 진입(Drawer) 클릭" = "click_drawer_login_register_enter",
  "로그인/회원가입 진입(내 장례) 클릭" = "click_mypage_login_register_enter",
  "장례식장 할인받기 클릭" = "click_parlor_detail_go_discount",
  "장례식장 할인여부 확인하기 클릭" = "click_parlor_detail_check_discount",
  "장례식장 상세 카카오톡 문의 클릭" = "click_parlor_detail_kakaotalk",
  "견적 요청서 작성 페이지 방문" = "uv_match_request",
  "내 맞춤 견적 페이지 방문" = "uv_consult_list_match",

  "견적 요청 - 장례준비긴급도 선택" = "click_match_request_1_status",
  "견적 요청 - 장지결정상태 선택" = "click_match_request_2_jangji_status",
  "견적 요청 - 자택/병원 주소 선택" = "click_match_request_3_location",
  "견적 요청 - 자택/병원 선택" = "click_match_request_3_0_location_choice",
  "견적 요청 - 장례 시도 선택" = "click_match_request_4_sido",
  "견적 요청 - 장례 시군구 선택" = "click_match_request_5_sigungu",
  "견적 요청 - 우대사항 선택" = "click_match_request_6_preferential",
  "견적 요청 화면 전환 클릭" = "click_match_request_next_conversion",
  "견적 요청 - 화장 여부 선택" = "click_match_request_7_hwajang",
  "견적 요청 - 빈소 여부 선택" = "click_match_request_8_needs_binso",
  "견적 요청 - 조문객 수 선택" = "click_match_request_9_condolers_count",
  "견적 요청 제출 클릭" = "click_match_request_submit_conversion",

  "견적 콘텐츠 - 상단 버튼 클릭" = "click_match_content_click_top",
  "견적 콘텐츠 - 고정 버튼 클릭" = "click_match_content_click_fixed",

  "장례식장 상세 모달 A - 상단 닫기" = "click_parlor_detail_modal_A_close_top",
  "장례식장 상세 모달 A - 하단 닫기" = "click_parlor_detail_modal_A_close_bottom",
  "장례식장 상세 모달 A - 자세히 알아보기" = "click_parlor_detail_modal_A_go_detail",
  "장례식장 상세 모달 A - 추천 받기" = "click_parlor_detail_modal_A_go_match_request",
  "장례식장 상세 모달 A - 관련 내용 더 알아보기" = "click_parlor_detail_modal_A_go_guidebook",

  "장례식장 상세 푸터 - 부고장" = "click_parlor_detail_footer_bugo",
  "장례식장 상세 푸터 - 비교하기" = "click_parlor_detail_footer_compare",
  "장례식장 상세 푸터 - 모달 닫기" = "click_parlor_detail_footer_close",
  "장례식장 상세 푸터 - 다른 장례식장 클릭" = "click_parlor_detail_footer_other_parlors",

  "가이드북 모달 A - 상단 닫기" = "click_guidebook_modal_A_close_top",
  "가이드북 모달 A - 하단 닫기" = "click_guidebook_modal_A_close_bottom",
  "가이드북 모달 A - 가이드북 받기" = "click_guidebook_modal_A_go_notice",

  "가이드북 신청 - 회원가입" = "click_notice_1_signup",
  "가이드북 신청 - 신청" = "click_notice_1_go_chat",

  "홈 배너 - 상단 클릭" = "click_home_banner_first",
  "홈 배너 - 하단 클릭" = "click_home_banner_second",

  "내 맞춤 견적 - 콘텐츠 클릭" = "click_list_match_click_content",

  "카테고리 고객 10명 중 9명이 추천한 상조 - 0" = "click_home_category_first_1",
  "카테고리 고객 10명 중 9명이 추천한 상조 - 1" = "click_home_category_first_2",
  "카테고리 고객 10명 중 9명이 추천한 상조 - 2" = "click_home_category_first_3",
  "카테고리 고객 10명 중 9명이 추천한 상조 - 3" = "click_home_category_first_4",
  "카테고리 고객 10명 중 9명이 추천한 상조 - 4" = "click_home_category_first_5",

  "카테고리 가격 만족도가 높은 상조 - 0" = "click_home_category_second_1",
  "카테고리 가격 만족도가 높은 상조 - 1" = "click_home_category_second_2",
  "카테고리 가격 만족도가 높은 상조 - 2" = "click_home_category_second_3",
  "카테고리 가격 만족도가 높은 상조 - 3" = "click_home_category_second_4",
  "카테고리 가격 만족도가 높은 상조 - 4" = "click_home_category_second_5",
  "카테고리 가격 만족도가 높은 상조 - 5" = "click_home_category_second_6",
  "카테고리 가격 만족도가 높은 상조 - 6" = "click_home_category_second_7",
  "카테고리 가격 만족도가 높은 상조 - 7" = "click_home_category_second_8",
  "카테고리 가격 만족도가 높은 상조 - 8" = "click_home_category_second_9",
  "카테고리 가격 만족도가 높은 상조 - 9" = "click_home_category_second_10",

  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 0" = "click_home_category_third_1",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 1" = "click_home_category_third_2",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 2" = "click_home_category_third_3",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 3" = "click_home_category_third_4",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 4" = "click_home_category_third_5",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 5" = "click_home_category_third_6",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 6" = "click_home_category_third_7",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 7" = "click_home_category_third_8",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 8" = "click_home_category_third_9",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 9" = "click_home_category_third_10",
  "카테고리 진정성에 감동 받은 후기가 많은 상조 - 10" = "click_home_category_third_11",

  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 0" = "click_home_category_fourth_1",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 1" = "click_home_category_fourth_2",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 2" = "click_home_category_fourth_3",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 3" = "click_home_category_fourth_4",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 4" = "click_home_category_fourth_5",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 5" = "click_home_category_fourth_6",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 6" = "click_home_category_fourth_7",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 7" = "click_home_category_fourth_8",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 8" = "click_home_category_fourth_9",
  "카테고리 전문성을 칭찬하는 후기가 많은 상조 - 9" = "click_home_category_fourth_10",

  "장례준비1 - 역경매 유도" = "click_funeral-prepare_match-request",

  "장례식장 상세 - 주소 복사" = "click_parlor_detail_copy_address",
  "장례식장 상세 - 후기 보기" = "click_parlor_detail_go_review",
  "장례식장 상세 - 탭 비용 클릭" = "click_parlor_detail_tab_price",
  "장례식장 상세 - 탭 정보 클릭" = "click_parlor_detail_tab_info",

  "장례식장 상세 - CTA" = "click_parlor_detail_CTA",
  "장례식장 상세 - 왜비싸" = "click_parlor_detail_why_expensive",
  "장례식장 상세 - 전체 후기 확인하기 클릭" = "click_parlor_detail_all_review",

  "장지 상세 - CTA" = "click_facility_detail_CTA",
  "장지 상세 - 탭클릭" = "click_facility_tab",

  "로그인 모달 - 카카오 로그인 클릭" = "click_login_modal_kakao_login",
  "로그인 모달 - 상단 닫기 클릭" = "click_login_modal_close_top",

  "프라임 - 선택 상품" = "payment_completed_product",
}

export enum PixelEventName {
  "InitiateCheckout" = "InitiateCheckout", // 계약서 화면
  "ViewContent" = "ViewContent", // CTA 클릭
  "CompleteRegistration" = "CompleteRegistration", // 본인인증
  "Purchase" = "Purchase", // 결제
  "Contact" = "Contact", // 문의
  "StartTrial" = "StartTrial", // 테스트 신청
  "AddToCart" = "AddToCart", // 조문객
  "AddToWishlist" = "AddToWishlist", // 본인 장례 제출
  "Lead" = "Lead", //콘텐츠 유효 타겟
  "Schedule" = "Schedule", // 상담 신청
  "AddPaymentInfo" = "AddPaymentInfo", // 플랫폼용 견적제출
  "CustomizeProduct" = "CustomizeProduct", // 플랫폼용 요양병원
  "Donate" = "Donate", // 플랫폼용 호스피스
  "Search" = "Search", // 플랫폼용 장지
}

export enum DableEventName {
  "Lead" = "Lead",
  "CompleteRegistration" = "CompleteRegistration",
  "PageView" = "PageView",
  "ViewContent" = "ViewContent",
  "Search" = "Search",
}

export enum NaverEventName {
  "Lead" = "3",
  "CompleteRegistration" = "4",
}

export enum TargetingGatesEventName {
  "Home" = "Home",
  "Join" = "Join",
  "PurchaseComplete" = "PurchaseComplete",
}
